/*
  Upgrade — trade-in quote engine
  ---------------------------------------------------------------------------
  Four customer questions (device / condition / functional fault / battery)
  identify ONE ROW in a precomputed table of outcomes. They are the same four
  questions assets/upgrade-sell.js asks, in the same words, against the same
  table — so the same customer with the same handset gets the same number
  whichever page they opened.

  THE OFFER IS LOOKED UP, NOT COMPUTED. Ruling, 22 September 2026: the browser
  gets outcomes, never inputs. assets/upgrade-tradein-data.json publishes, per
  variant, a fixed 32-slot row covering every combination of the four
  condition answers, four battery answers and two fault answers. This file
  reads the slot the customer's answers select and shows it. Nothing it
  receives says how that figure was arrived at, and nothing here reconstructs
  it — there is no acquisition price, no component amount and no ladder
  fraction in the browser to reconstruct it from.

  THE SALE PRICE OF WHAT THEY ARE BUYING IS SHOPIFY'S, NOT THIS FILE'S.
  Ruling, Omar Hussein, 22 September 2026. Every EGP figure on a trade-up card
  — the target device's price, the bonus computed from it and the estimated
  balance that follows — is read from window.UpgradePrices, the live variant
  price map rendered server-side once per page in layout/theme.liquid. This
  file no longer reads variant.list from assets/upgrade-tradein-data.json for
  any figure: the site used to show one price and charge another, and they
  agreed only by luck. The bonus formula is unchanged; only the list price it
  is computed from moved. A price the map cannot supply is null, and null
  renders as the hotline state — never a blank, never 0 EGP, never the figure
  the surface was holding a moment ago.

  REFUSAL IS A PUBLISHED FACT, not a judgement made here. An outcome the
  business will not quote without having seen the phone arrives as a null cash
  value AND a band code of meta.offer_refused_band — stated twice, so missing
  one check still refuses. This file then shows no figure and routes to a
  person. Ruling R2.

  Every rate and every rule this file uses — the trade-up bonus percentage,
  its cap, the lookup axes, the index strides and the band labels — is read
  from the meta block of assets/upgrade-tradein-data.json. None of them is
  restated here, in code or in prose, so there is exactly one place to change
  one. Ruling R3.

  It is read with the SAME field preference as assets/upgrade-deal.js and
  assets/upgrade-sell.js — bonus_pct / bonus_cap first, falling back to the
  discount_pct / discount_cap the block publishes today — and with the same
  validation. Identical preference is not tidiness: upgrade-deal.js draws the
  sticky bar that sits on the same screen as these cards, so a file reading one
  field while the bar reads another quotes two bonuses for one deal the day a
  bonus_pct is added to the data. Identical validation is not tidiness either:
  an absent or malformed rate must suppress the figure, never reach a customer
  as a computed number.

  TWO COMPONENTS, AND ONLY ONE OF THEM IS SOFT. Every surface in this file
  that quotes a trade-up must show them separately:

    the customer's phone valuation   ESTIMATE  — it rests on four answers the
      customer gave about a phone nobody has seen, and the real figure lands
      at the physical inspection. This line, and only this line, carries the
      "confirmed at inspection" caveat.
    the trade-up bonus               GUARANTEED — a function of the device
      being BOUGHT and nothing else, known the moment they pick one. It does
      not depend on the condition, grade, battery or age of the old phone, so
      it is never written as an estimate, as indicative, or as "up to".

  The bonus is ADDED TO THE VALUE OF THE CUSTOMER'S PHONE. That is the whole
  mechanic, and the reason the bonus exists:

    sell     → phone value                     paid as CASH
    trade in → phone value + bonus             given as CREDIT against the new
                                               device

  So it is presented as making THEIR PHONE WORTH MORE, not as a discount on
  the purchase, and it is never netted silently into a single "you pay"
  figure. Internally it is the standing website discount redirected rather
  than an extra cost — margin-neutral by construction — but that is a margin
  fact, not something the customer-facing copy restates.

  Payment is on delivery, the cart charges the full price, and nothing here
  writes a discount. The balance is therefore still called ESTIMATED — the
  phone valuation feeds it — and it is settled at the inspection. Ruling R1.
  Since 28 Sep 2026 (LOG-021) the journey ends in an ORDER for the new phone,
  with the trade-in written onto its cart line as notes (UpgradeDeal.order),
  and the person who delivers checks the old phone at the door.
  ---------------------------------------------------------------------------
*/
(function () {
  'use strict';
  /* ---- ARABIC PHASE 3, 4 Oct 2026 (LOG-043; 2026-10-04_arabic/phase3-tradein-sell.md)
     Every customer word this tool prints is in EN below, exactly as it was
     written inline before, read through T(). An English page has no
     dictionary, so T() returns EN's text and the English output is
     identical by construction (tests/engines.test.js, one-price,
     tradeup-balance and totals run this file with no window.UpgradeI18n).
     On an Arabic page T() reads window.UpgradeI18n, falling back to EN per
     key. tools/build-i18n.js reads this table ("area.name" is the locale
     key upgrade.js.area.name_html).
     WHAT STAYS ENGLISH DATA, in every language: the valuation handed to
     window.UpgradeDeal.setTradein() (keys and numbers only), the target's
     label / gradeLabel / storage written by setTarget() (GRADE_LABELS below:
     the deal bar names the grade from target.grade on a translated page),
     the console diagnostics, and the session source record.
     FIGURES: fig() / money() / plusFig() are the display formatters. English:
     egp(n) (+ ' EGP'), exactly as before. Arabic: the page's number locale
     (Western digits, LOG-043) isolated on right-to-left pages, the sign
     inside the isolate, the currency word from common.money (ج.م).
     LINKS: routeRoot() keeps the language prefix ('/' on English, '/ar/' on
     Arabic), the same rule as assets/upgrade-deal.js. */
  var EN = {
    'common.grade_g': 'Gold',
    'common.grade_p': 'Platinum',
    'common.grade_d': 'Diamond',
    'common.money': '{amount} EGP',
    'common.currency': 'EGP',
    'tradein.cond_flawless_title': 'Flawless',
    'tradein.cond_flawless_desc': 'No scratches, no dents. Looks unused.',
    'tradein.cond_good_title': 'Good',
    'tradein.cond_good_desc': 'Light marks on the screen, frame or back. No dents.',
    'tradein.cond_fair_title': 'Fair',
    'tradein.cond_fair_desc': 'Heavy scratches or small dents. Everything works.',
    'tradein.cond_damaged_title': 'Damaged',
    'tradein.cond_damaged_desc': 'Cracked screen or broken body.',
    'tradein.batt_96plus': '96% or more',
    'tradein.batt_91to95': '91–95%',
    'tradein.batt_90orless': '90% or less',
    'tradein.batt_unsure': "Don't know",
    'tradein.call_to_order': 'Call {hotline} to order',
    'tradein.priced_batt_96plus': 'battery 96% or more',
    'tradein.priced_batt_91to95': 'battery 91–95%',
    'tradein.priced_batt_90orless': 'battery 90% or less',
    'tradein.priced_batt_unsure': 'battery health not known',
    'tradein.priced_condition': '{condition} condition',
    'tradein.priced_fault_yes': 'a functional fault',
    'tradein.priced_fault_no': 'no faults',
    'tradein.list_separator': ', ',
    'tradein.your_phone_estimate': 'Your {phone} — estimate',
    'tradein.bonus_guaranteed': 'Trade-up bonus — guaranteed',
    'tradein.total_credit_amount': '{amount} EGP total credit',
    'tradein.credit_applied_note': 'Your phone\'s value is confirmed when we check it at delivery; the trade-up bonus is set by the device you are buying and does not move when we check your phone. Choose your options on the next page and add it to your cart: your trade-in goes with the order. The cart shows the full price, and at delivery you pay only the difference.',
    'tradein.view_this_device': 'View this device',
    'tradein.trading_up_to': 'Trading up to',
    'tradein.change': 'change',
    'tradein.step_device': 'Device',
    'tradein.step_condition': 'Condition',
    'tradein.step_battery': 'Battery',
    'tradein.step_offer': 'Your offer',
    'tradein.device_title': 'Which device are you trading in?',
    'tradein.another_model': 'Another model? {link} and we\'ll quote it by hand.',
    'tradein.call_our_hotline': 'Call our hotline, {hotline}',
    'tradein.call_our_hotline_lower': 'call our hotline, {hotline}',
    'tradein.condition_title': 'What condition is it in?',
    'tradein.fault_question': 'Separately — does it have a functional fault that is not about looks (for example: speaker, microphone, camera, buttons, or charging)?',
    'tradein.fault_label': 'Functional fault',
    'tradein.fault_no': 'No, everything works',
    'tradein.fault_yes': 'Yes, it has a fault',
    'tradein.battery_help_samsung': "Samsung phones don't show a percentage — choose Don't know.",
    'tradein.battery_help_iphone15': 'iPhone: Settings → Battery → Battery Health.',
    'tradein.battery_help_iphone14': 'iPhone: Settings → Battery → Battery Health &amp; Charging.',
    'tradein.battery_title': 'Battery health?',
    'tradein.offer_title': 'Your offer',
    'tradein.select_storage': 'Select a storage size to see your offer.',
    'tradein.refused_title': 'We need to see this one first',
    'tradein.refused_fault': "We don't price a phone with a fault online, so your {phone} needs to be seen first. Rather than show you a number we might not honour, one of our buyers will price it by hand.",
    'tradein.refused_floor': 'Your {phone} in this condition falls below the lowest offer we will quote without seeing the phone. Rather than show you a number we might not honour, one of our buyers will price it by hand.',
    'tradein.summary_phone_estimate': 'Your {phone} — estimate, confirmed at delivery',
    'tradein.summary_total_credit': 'Total credit toward your new phone',
    'tradein.summary_target_price': '{device} price',
    'tradein.estimated_balance': 'Estimated balance',
    'tradein.covered_full': 'Your credit covers this device in full – we pay you the difference at delivery',
    'tradein.covered_exact': 'Your credit covers this device in full. Nothing to pay at delivery, once your phone is checked.',
    'tradein.estimated_balance_to_pay': 'Estimated balance to pay',
    'tradein.or_with_valu': 'Or in instalments with one of our partners',
    'tradein.summary_note_covered': 'Your phone\'s value is an estimate until we have seen it. The trade-up bonus is not — it is set by the device you are buying, so it does not move. Order the new phone with your trade-in: the cart shows the full price and nothing is paid until delivery. When we deliver, we check your {phone} in front of you — if the phone matches what you told us, your credit covers this device in full and we pay you the difference. If you would rather keep your phone, you can still take the new one at the full price.',
    'tradein.summary_note_exact': 'Your phone\'s value is an estimate until we have seen it. The trade-up bonus is not — it is set by the device you are buying, so it does not move. Order the new phone with your trade-in: the cart shows the full price and nothing is paid until delivery. When we deliver, we check your {phone} in front of you — if the phone matches what you told us, your credit covers this device in full. Nothing to pay at delivery. If you would rather keep your phone, you can still take the new one at the full price.',
    'tradein.summary_note_balance': 'Your phone\'s value is an estimate until we have seen it. The trade-up bonus is not — it is set by the device you are buying, so it does not move. Order the new phone with your trade-in: the cart shows the full price and nothing is paid until delivery. When we deliver, we check your {phone} in front of you — if the phone matches what you told us, the balance above is what you pay, in cash, by card or in instalments with one of our partners. If you would rather keep your phone, you can still take the new one at the full price.',
    'tradein.order_cta': 'Order with trade-in',
    'tradein.adding': 'Adding…',
    'tradein.order_failed': 'We could not add that to your cart just now. Please try again in a moment, or call {hotline}.',
    'tradein.view_cart': 'View cart',
    'tradein.choose_phone': 'Choose your phone',
    'tradein.sell_for_cash': 'Sell it for cash',
    'tradein.paid_at_visit': 'Paid at the visit, in cash or by transfer.',
    'tradein.sell_instead': 'Sell it instead',
    'tradein.trade_towards': 'Or trade it in towards {device}',
    'tradein.hero_explainer': 'Your {value} EGP valuation plus a guaranteed {bonus} EGP trade-up bonus, as credit. The bonus is set by the device you pick — each one below shows its own.',
    'tradein.estimate_caveat': 'Your phone\'s value above is an estimate, confirmed when we check the phone: at your door when we deliver a new one, or at the visit if you sell it. If the phone matches what you told us, that is what you get. The trade-up bonus is not an estimate: it is set by the device you are buying, so it does not move when we check your phone.',
    'tradein.what_we_priced': 'What we priced',
    'tradein.your_phone_value': 'Your phone\'s value',
    'tradein.what_we_priced_summary': 'What we priced: {summary}.',
    'tradein.inspection_confirms': 'Confirmed when we check your phone — if it matches what you told us, that is the value we honour, as cash if you sell or as credit if you trade in.',
    'tradein.ladder_band': 'Checked against our trade-in ladder: this offer sits in the {band} band.',
    'tradein.ladder_below_average': 'Checked against our trade-in ladder: this offer sits below the Average band but above the lowest value we quote online, so it stands.',
    'tradein.band_f': 'Flawless',
    'tradein.band_g': 'Good',
    'tradein.band_a': 'Average',
    'tradein.trade_up_to': 'Trade up to',
    'tradein.total_credit': 'Total credit',
    'tradein.card_balance': 'Estimated balance {amount} EGP',
    'tradein.choose_this': 'Choose this',
    'tradein.bonus_explainer': 'The trade-up bonus is added to what your phone is worth. Sell it outright and you get its value in cash; trade it in and you get its value plus the bonus, as credit against the device you are buying. The bonus is set by the device you choose — not by the age or condition of your old phone — so it is fixed from the moment you pick one.',
    'tradein.trade_up_instead': 'Trade up instead',
    'tradein.newest_phone': 'Your {device} is one of the newest phones we take, so there is nothing later than it in stock right now to trade up to. You can still put it toward any device we sell — {shop_link}, or {hotline_link}. Your valuation follows you, so you are not asked these questions again.',
    'tradein.browse_full_range': 'browse the full range',
    'tradein.put_toward': 'You can put your {device} toward a refurbished phone instead of taking the cash. {shop_link}, or {hotline_link}.',
    'tradein.browse_title': 'Want a different phone?',
    'tradein.browse_note': 'Pick any device we sell. Your valuation follows you, so you are not asked these questions again.',
    'tradein.browse_every_device': 'Browse every device we have',
    'tradein.start_again': 'Start again',
    'tradein.pricing_unavailable': 'Trade-in pricing is unavailable right now.',
    /* ARABIC PORT pA, 7 Oct 2026 (2026-10-04_arabic/port-2026-10-07/pA.md).
       - The 23 words the English build moved into section settings (Lynkrs
         batch 2, W1a: tool_strip_device ... tool_unavailable, handed over as
         data-t-* attributes and read through TX()) KEEP their keys above:
         they are TEXT's words below, i.e. what TX() falls back to when an
         attribute is missing or a heading is emptied, in the page's
         language. Their Arabic for the SETTINGS themselves is theme content
         (rule R1; port-2026-10-07/pA-theme-content.json).
       - tradein.covered_full carries the batch-2 en dash (D2), and the three
         summary_note_* sentences lose the box sentence and the ID/sign-out
         parenthesis (Lynkrs Q10: the one checklist follows them).
       - New sentences of the English build, each with a number or a date,
         so keys and not settings (rule R4): the step count (Q6) and the saved
         estimate (PG-TradeIn-3). */
    'tradein.step_count': 'Step {n} of {total}',
    'tradein.saved_from': 'Saved estimate from {date}',
    'tradein.saved': 'Saved estimate',
    'tradein.saved_note': 'We kept your answers on this device and checked them against our current price list. The value is still an estimate, confirmed when we check your phone. It is kept until our price list changes, for {days} at most. The trade-up bonus is not saved: it is set by the phone you choose.',
    'tradein.kept_line': 'Your answers are saved on this device until our price list changes, for {days} at most, so you can come back to this estimate.',
    'tradein.saved_days': { one: '{count} day', other: '{count} days' },
    'tradein.saved_date': '{day} {month} {year}',
    'tradein.month_1': 'Jan',
    'tradein.month_2': 'Feb',
    'tradein.month_3': 'Mar',
    'tradein.month_4': 'Apr',
    'tradein.month_5': 'May',
    'tradein.month_6': 'Jun',
    'tradein.month_7': 'Jul',
    'tradein.month_8': 'Aug',
    'tradein.month_9': 'Sep',
    'tradein.month_10': 'Oct',
    'tradein.month_11': 'Nov',
    'tradein.month_12': 'Dec'
  };
  var I18N = (typeof window !== 'undefined' && window.UpgradeI18n) || null;
  /* ---- EDIT EVERYTHING, 7 Oct 2026 (Omar: "my content team needs to be able
     to edit anything"; 2026-10-06_lynkrs-tech-fixes/edit-everything-7oct/
     build/tradein.md). The rest of this tool's words are settings of the
     section too (Theme editor > Trade-in calculator > "Answers in the
     trade-in tool", "Offer, sums and buttons in the trade-in tool",
     "Promise notes in the trade-in tool"), printed by
     sections/upgrade-tradein.liquid as one inert tag,
     <script type="application/json" data-ug-tradein-words>, keyed by the EN
     key. OWN() hands T() the section's word only when:
       - it is a string with something in it (an emptied field shows the
         standard words: no promise disappears by accident);
       - its {placeholders} are exactly EN's, none missing and none unknown;
       - its digits outside the placeholders are EN's (the battery answers'
         96 / 91 / 95 / 90 cannot drift away from the price table's bands);
       - on a translated page, it is not EN's own text: a setting with no
         registered translation reaches /ar as the English value, and the
         dictionary's Arabic shows instead, exactly as before.
     Otherwise T() is what it was: the dictionary, then EN. EN stays word for
     word, as the fallback and as the input of tools/build-i18n.js. The
     section's words are plain text: T() escapes them for the innerHTML
     sinks (EN is already markup-ready: "&amp;"), TT() hands them raw to the
     textContent and attribute sinks; the values filled into them are
     isolated on right-to-left pages, as UpgradeI18n.t() does. No figure is
     a word: every number still comes from the engine below. */
  var OWN_WORDS = readOwnWords();
  function readOwnWords() {
    try {
      if (typeof document === 'undefined' || !document || typeof document.querySelector !== 'function') { return {}; }
      var el = document.querySelector('script[data-ug-tradein-words]');
      if (!el) { return {}; }
      var o = JSON.parse(el.textContent || '{}');
      return (o && typeof o === 'object') ? o : {};
    } catch (e) { return {}; }
  }
  function placeholdersOf(s) {
    var seen = {};
    (String(s).match(/\{\w+\}/g) || []).forEach(function (p) { seen[p] = true; });
    return Object.keys(seen).sort().join(',');
  }
  function digitsOf(s) {
    return (String(s).replace(/\{\w+\}/g, '').match(/\d+/g) || []).sort().join(',');
  }
  function OWN(key) {
    var v = Object.prototype.hasOwnProperty.call(OWN_WORDS, key) ? OWN_WORDS[key] : null;
    var en = EN[key];
    if (typeof v !== 'string' || !/\S/.test(v) || typeof en !== 'string') { return null; }
    if (placeholdersOf(v) !== placeholdersOf(en) || digitsOf(v) !== digitsOf(en)) { return null; }
    if (I18N && escText(v) === en) { return null; }
    // FIX PASS, 7 Oct 2026 (fixes/FIXES.md D27): on a translated page a field is used only when it carries
    // Arabic script, or the dictionary has no such key. Shopify serves a field's ENGLISH until its Arabic is
    // registered, so an English edit made before that never reaches /ar: the dictionary's Arabic shows.
    if (I18N && !/[\u0600-\u06FF]/.test(v)) {
      try { if (I18N.has(key)) { return null; } } catch (eA) { return null; }
    }
    return v;
  }
  // {name} -> value, each value isolated on a right-to-left page (once).
  function fillOwn(str, vars) {
    return String(str).replace(/\{(\w+)\}/g, function (whole, name) {
      return (vars && Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== null && vars[name] !== undefined)
        ? isolateText(vars[name]) : whole;
    });
  }
  // {name} -> value; an unknown {name} is left as it is.
  function fill(str, vars) {
    return String(str == null ? '' : str).replace(/\{(\w+)\}/g, function (whole, name) {
      return (vars && Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== null && vars[name] !== undefined)
        ? String(vars[name]) : whole;
    });
  }
  // TT(): the same word for a textContent or attribute sink -- the section's
  // word unescaped; otherwise exactly T().
  function TT(key, vars) {
    var own = OWN(key);
    if (own !== null) { return fillOwn(own, vars); }
    return T(key, vars);
  }
  /* Three sentences the summary box shares, byte for byte, with the deal bar
     in the same viewport: the deal bar's words section owns them
     (dealSays() below), and this file's own words show when it has none. */
  var SHARED_WITH_DEAL = {
    'tradein.estimated_balance': 'estimated_balance',
    'tradein.covered_full': 'covered_full',
    'tradein.covered_exact': 'covered_exact'
  };
  // The section's word (edit everything), else the dictionary's text when it
  // has the key, else EN's. Never throws.
  function T(key, vars) {
    var shared = Object.prototype.hasOwnProperty.call(SHARED_WITH_DEAL, key) ? dealSays(SHARED_WITH_DEAL[key]) : null;
    if (shared !== null) { return shared; }
    var own = OWN(key);
    if (own !== null) { return fillOwn(escText(own), vars); }
    if (I18N) {
      try {
        if (I18N.has(key)) {
          var tr = I18N.t(key, vars);
          if (typeof tr === 'string') { return tr; }
        }
      } catch (e) { /* fall back to English */ }
    }
    return fill(EN[key], vars);
  }
  function rtl() {
    try { return !!(I18N && typeof I18N.dir === 'function' && I18N.dir() === 'rtl'); } catch (e) { return false; }
  }
  // Bidi isolation of a value set as text, on right-to-left pages only.
  // English: the value, untouched.
  function isolateText(value) {
    var str = String(value == null ? '' : value);
    try {
      if (rtl() && typeof I18N.isolate === 'function') { return I18N.isolate(str); }
    } catch (e) { /* English behaviour */ }
    return str;
  }
  // A DISPLAY figure: in the page's number locale (Western digits on Arabic,
  // LOG-043) and isolated on right-to-left pages, `sign` inside the isolate.
  // English: egp(n), exactly.
  function fig(n, sign) {
    var s = sign || '';
    if (I18N && typeof I18N.num === 'function') {
      var d = null;
      try { d = I18N.num(Math.round(n || 0)); } catch (e) { d = null; }
      if (typeof d === 'string') { return isolateText(s + d); }
    }
    return s + egp(n);
  }
  // "<figure> EGP" in the page language (common.money). English: egp(n) + ' EGP'.
  function money(n) {
    return T('common.money', { amount: fig(n) });
  }
  // The bonus as an added figure. English: '+ ' + egp(n), exactly as before.
  // Translated: the + sign inside the isolate, with no space, so it cannot
  // be separated from its number in right-to-left text.
  function plusFig(n) {
    if (I18N && typeof I18N.num === 'function') { return fig(n, '+'); }
    return '+ ' + egp(n);
  }
  // A grade's display name from its code. English: GRADE_LABELS' words.
  function gradeName(code) {
    return T('common.grade_' + code);
  }
  // The offer table's band name (meta.offer_bands) in the page language:
  // the band CODE names the key on a translated page; English prints the
  // data's own label exactly as before.
  function bandName(band) {
    if (!band) { return null; }
    if (I18N) {
      var k = 'tradein.band_' + String(band.key || '').toLowerCase();
      try { if (I18N.has(k)) { return T(k); } } catch (e) { /* English behaviour */ }
    }
    return band.label;
  }
  // The language prefix for links this file builds: '/' on English, '/ar/'
  // on Arabic (Shopify.routes.root). Same rule as assets/upgrade-deal.js.
  function routeRoot() {
    var r = (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }
  /* ARABIC PORT pA, 7 Oct 2026. Three helpers the English build's new code
     needs in this copy.
     localHref(): a site path from window.UpgradeRoutes (Lynkrs B6:
       Shopify's product.url, or '/pages/shop') keeps the language prefix on
       a translated page. A path already carrying it, an absolute or
       protocol-relative URL, and anything on an English page (routeRoot() is
       '/') are returned exactly as given.
     TN(): a plural, the same helper as assets/upgrade-deal.js. EN[key] is
       { one, other }; the dictionary carries key_zero ... key_other.
     savedDay(): the saved estimate's day, for display. English (no
       dictionary): UpgradeDeal.quoteDate(), the English build's own call,
       exactly. A translated page names the month from tradein.month_1 ...
       month_12 and keeps Western digits (LOG-043). */
  function localHref(u) {
    var s = String(u == null ? '' : u);
    var r = routeRoot();
    if (r === '/' || s.charAt(0) !== '/' || s.charAt(1) === '/' || s.indexOf(r) === 0 || s + '/' === r) return s;
    return r + s.slice(1);
  }
  function TN(key, n, vars) {
    var v = {};
    Object.keys(vars || {}).forEach(function (k) { v[k] = vars[k]; });
    if (v.count === undefined) { v.count = n; }
    if (I18N) {
      try {
        if (I18N.has(key + '_other')) {
          var tr = I18N.plural(key, n, v);
          if (typeof tr === 'string') { return tr; }
        }
      } catch (e) { /* fall back to English */ }
    }
    var forms = EN[key];
    if (forms && typeof forms === 'object') { return fill(n === 1 ? forms.one : forms.other, v); }
    return fill(forms, v);
  }
  function savedDay(api, ts) {
    if (I18N && typeof ts === 'number' && isFinite(ts)) {
      try {
        if (I18N.has('tradein.month_1') && I18N.has('tradein.saved_date')) {
          var d = new Date(ts);
          return T('tradein.saved_date', { day: String(d.getDate()), month: T('tradein.month_' + (d.getMonth() + 1)), year: String(d.getFullYear()) });
        }
      } catch (e) { /* English behaviour */ }
    }
    return (api && typeof api.quoteDate === 'function') ? api.quoteDate(ts) : '';
  }

  function ugAssetBase() {
    var el = document.querySelector('[data-asset-base]');
    return el ? (el.getAttribute('data-asset-base') || '') : '';
  }

  var root = document.querySelector('[data-upgrade-tradein]');
  if (!root) return;

  // Upgrade's one confirmed, sourced contact number (00-COMPANY/01-company-
  // profile.md), already used across this theme. The trade-in section carries
  // no verified WhatsApp number, so every "talk to a person" route here is the
  // hotline — house rule: never invent a contact number.
  var HOTLINE = (root.dataset.hotline || '19759').replace(/[^0-9]/g, '') || '19759';

  function contactHref() { return 'tel:' + HOTLINE; }

  var GRADE_LABELS = { g: 'Gold', p: 'Platinum', d: 'Diamond' };

  // Trade-up cards are priced on the Platinum list price of the storage the
  // card names. One constant, so the badge, the pinned target and the handoff
  // link can never name three different grades.
  var CARD_GRADE = 'p';

  /* ---- words the content lead edits (Lynkrs batch 2, W1a, 6 Oct 2026) ----
     Batch E2. The tool's step labels, headings, buttons and two notes are
     settings of the section (Theme editor > Trade-in calculator > "Words in
     the trade-in tool" / "Promises in the trade-in tool"), handed over as
     data-t-* attributes on the mount (sections/upgrade-tradein.liquid). TEXT
     is today's English, word for word: a missing attribute (an older section
     file, a test harness) reads it, so the page is unchanged until she edits.
     A heading or button she empties keeps these words; a note she empties
     (TEXT_LINES) is left out and TX() returns ''. The same pattern as
     assets/upgrade-sell.js (W1b), so the two tools are edited the same way.
     What stays here and why: the answers and their descriptions, the fault
     question and the battery help (the price lookup's own questions, word for
     word the sell tool's), the offer, the trade-in sums, the estimate and
     guarantee notes (R1), every sentence carrying a figure, a date, a phone's
     name or the hotline (Arabic rule R4), and "Step N of M" (numbers, R4).
     SUPERSEDED, 7 Oct 2026 (edit everything; the paragraph above is kept as
     the record): Omar's instruction overrides R4 here. Those words are
     settings too, read through T() / OWN() above, guarded so that a
     placeholder or a figure can never be lost: see the top of this file. */
  /* Arabic port (pA, 7 Oct 2026): TEXT reads the words from EN through T(),
     so a missing attribute or an emptied heading falls back to the page's
     own language. On an English page T() returns EN's text, which is the
     English build's TEXT word for word (tests/ar-port-pA.test.js pins all
     23), so the page is unchanged. */
  var TEXT = {
    stripDevice: T('tradein.step_device'),
    stripCondition: T('tradein.step_condition'),
    stripBattery: T('tradein.step_battery'),
    stripOffer: T('tradein.step_offer'),
    deviceTitle: T('tradein.device_title'),
    conditionTitle: T('tradein.condition_title'),
    batteryTitle: T('tradein.battery_title'),
    resultTitle: T('tradein.offer_title'),
    storageHint: T('tradein.select_storage'),
    targetEyebrow: T('tradein.trading_up_to'),
    changeLabel: T('tradein.change'),
    cashHeading: T('tradein.sell_for_cash'),
    cashLine: T('tradein.paid_at_visit'),
    sellLink: T('tradein.sell_instead'),
    pricedHeading: T('tradein.what_we_priced'),
    railHeading: T('tradein.trade_up_to'),
    chooseLabel: T('tradein.choose_this'),
    viewLabel: T('tradein.view_this_device'),
    browseTitle: T('tradein.browse_title'),
    browseText: T('tradein.browse_note'),
    browseButton: T('tradein.browse_every_device'),
    resetLabel: T('tradein.start_again'),
    unavailable: T('tradein.pricing_unavailable')
  };
  var TEXT_LINES = { cashLine: true, browseText: true };

  function TX(key) {
    var ds = (root && root.dataset) || {};
    var v = ds['t' + key.charAt(0).toUpperCase() + key.slice(1)];
    if (typeof v !== 'string') return TEXT[key];
    if (/\S/.test(v)) return v;
    return TEXT_LINES[key] ? '' : TEXT[key];
  }

  /* Her words go into markup that h() writes as innerHTML, so they are
     escaped: what she types is what shows, never markup. Today's words carry
     none of these characters, so the escaped default is byte-identical. */
  function escText(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function TXH(key) { return escText(TX(key)); }

  /* Edit everything (7 Oct 2026): three sentences the summary box shares,
     byte for byte, with the deal bar in the same viewport (the balance row's
     name and the two "covers this device in full" sentences). The bar's
     words section owns them: UpgradeDeal.say() returns its word, or null
     when that section is not on the page or the word is neither edited nor
     translated, and then this file's own words show, exactly as before.
     Escaped: the summary rows are innerHTML. */
  function dealSays(name) {
    try {
      var api = (typeof window !== 'undefined') ? window.UpgradeDeal : null;
      var w = (api && typeof api.say === 'function') ? api.say(name) : null;
      return (typeof w === 'string' && /\S/.test(w)) ? escText(w) : null;
    } catch (e) { return null; }
  }

  function blankState() {
    return { brand: null, series: null, storage: null, condition: null, battery: null, faulty: null, step: 1 };
  }

  var DATA = null;
  var S = blankState();

  /* LF8 fix (29 Sep 2026, fix_now, audit w4k72rk5w). This page's own result
     step promises "your valuation follows you, so you are not asked these
     questions again" -- but S always started blank(), so a customer who
     left this page (to look at a product, say) and came back saw question
     1 again, even with a live valuation already parked in
     window.UpgradeDeal. Called once from boot, after DATA has loaded:
     restores S from that SAME record setTradein() already writes (series,
     storage, condition, battery, faulty; brand is looked up from
     DATA.devices by series, since the record itself does not carry one)
     and jumps to step 4. The cash figure itself is never copied in -- S
     has no `value` field at all, so stepResult() always recomputes it
     fresh from S through the real engine on this render, exactly as it
     does on every other one; "re-run the engine, never trust the stored
     figure" is the natural result of restoring ANSWERS, not the answer.
     No-ops (S left as-is) with no live trade-in, no DATA yet, or a series
     the current catalogue no longer carries. */
  function restoreFromLiveTradein() {
    if (!DATA || !DATA.devices) { return; }
    if (!window.UpgradeDeal || typeof window.UpgradeDeal.get !== 'function') { return; }
    var rec;
    try { rec = window.UpgradeDeal.get().tradein; } catch (e) { rec = null; }
    if (!rec || !rec.series) { return; }
    var dev = null;
    DATA.devices.forEach(function (d) { if (d.series === rec.series) dev = d; });
    if (!dev) { return; }
    /* RESTORE GUARD, 1 Oct 2026 (the battery answers moved to the counter's
       bands). An answer is restored only if it is still on the published axis
       (meta.offer_axes). One that is not -- a September battery key such as
       above90, or anything unrecognised -- is DROPPED and asked again, never
       translated: guessing that "above 90%" means "96% or more" would price a
       phone at a band the customer never chose. The journey resumes at the
       first question whose answer did not survive. An `unsure` from the
       September bands WOULD pass this check (same key, new meaning); it never
       arrives here only because the deal store is ug.deal.v2 and v1 is never
       read, so every record this function sees was written under the new
       bands. */
    var axes = DATA.meta && DATA.meta.offer_axes;
    function onAxis(axis, value) {
      return !!(axes && axes[axis] && value !== null && value !== undefined && axes[axis].indexOf(value) !== -1);
    }
    var condition = onAxis('condition', rec.condition) ? rec.condition : null;
    var battery = condition && onAxis('battery', rec.battery) ? rec.battery : null;
    S = {
      brand: dev.brand,
      series: rec.series,
      storage: rec.storage || null,
      condition: condition,
      battery: battery,
      faulty: condition ? !!rec.faulty : null,
      step: !condition ? 2 : (!battery ? 3 : 4)
    };
  }

  /* ---- the saved estimate (Lynkrs PG-TradeIn-3 / PSY-Endowment, batch 2,
     W1a, 6 Oct 2026) ---------------------------------------------------------
     A valuation used to live only in this tab's session: a new tab, or the
     next day, asked the questions again. assets/upgrade-deal.js now keeps the
     ANSWERS of the last valuation on the device (UpgradeDeal.savedEstimate(),
     localStorage), with the figure they gave and the day they were given,
     until the price list changes or for 7 days at most (the rules are in
     that file, at SAVED_KEY).

     NEVER TRUSTED FOR PRICE. restoreFromSaved() runs only when this tab has no
     live valuation, and only on this page, because this page has the engine:
     it puts the saved answers back, asks quote() again, and keeps them only
     when every answer is still on the published axes, the phone and storage
     are still quoted online, the table does not refuse them, and the figure is
     exactly the saved one. Anything else -- another price list, a figure the
     table no longer gives, a phone withdrawn, a record somebody edited -- and
     the record is forgotten and the questions start again. The figure on
     screen is always the one quote() just looked up; the saved figure is only
     the check. The bonus is never saved: it is set by the phone being bought,
     at the moment it is chosen, exactly as before.

     The result then says so: "Saved estimate from <date>", that it is still
     an estimate, how long it is kept, and a "Start again" beside it. The
     notice describes the restored answers only (savedFrom.sig): change any
     answer and it is a new estimate, with no notice. A new estimate says, in
     one line, that it is kept on this device, but only once the device has
     actually kept it (private windows keep nothing, and are told nothing). */
  var savedFrom = null;

  function answersSig(a) {
    return [a.series, a.storage, a.condition, a.battery, a.faulty === true ? '1' : '0'].join('|');
  }

  function savedApi() {
    var api = (typeof window !== 'undefined') ? window.UpgradeDeal : null;
    return (api && typeof api.savedEstimate === 'function') ? api : null;
  }

  function restoreFromSaved() {
    savedFrom = null;
    if (!DATA || !DATA.devices || S.series) return;   // a live valuation in this tab wins
    var api = savedApi();
    if (!api) return;
    var rec = null;
    try { rec = api.savedEstimate(); } catch (e) { rec = null; }
    if (!rec) return;
    function drop() {
      try { if (typeof api.forgetEstimate === 'function') api.forgetEstimate(); } catch (e) { /* nothing to forget */ }
      S = blankState();
    }
    var axes = DATA.meta && DATA.meta.offer_axes;
    function onAxis(axis, value) {
      return !!(axes && axes[axis] && axes[axis].indexOf(value) !== -1);
    }
    var dev = null, v = null;
    DATA.devices.forEach(function (d) { if (d.series === rec.series) dev = d; });
    if (dev && dev.variants) dev.variants.forEach(function (x) { if (x.storage === rec.storage && x.tradein !== false) v = x; });
    if (!dev || !dev.tradein || !v || typeof rec.faulty !== 'boolean' ||
        !onAxis('condition', rec.condition) || !onAxis('battery', rec.battery) || !onAxis('fault', rec.faulty)) {
      drop();
      return;
    }
    S = {
      brand: dev.brand, series: dev.series, storage: v.storage,
      condition: rec.condition, battery: rec.battery, faulty: rec.faulty, step: 4
    };
    var q = quote();
    if (!q || q.refused || typeof q.cash !== 'number' || q.cash !== rec.value) {
      drop();
      return;
    }
    savedFrom = { sig: answersSig(S), savedAt: rec.savedAt };
  }

  function savedDays(api) {
    var n = api && api.savedDays;
    return (typeof n === 'number' && n > 0) ? n : 7;
  }

  /* The notice at the top of a restored result, or null. */
  function savedNotice() {
    var api = savedApi();
    if (!api || !savedFrom || savedFrom.sig !== answersSig(S)) return null;
    // Arabic port (pA): the words are EN tradein.saved_* (keys, rule R4: a
    // date and a number); the day is savedDay(), which is quoteDate() on an
    // English page, exactly.
    var day = savedDay(api, savedFrom.savedAt);
    var box = h('div', 'ug-saved');
    box.appendChild(h('p', 'ug-saved__k', day ? T('tradein.saved_from', { date: escText(day) }) : T('tradein.saved')));
    box.appendChild(h('p', 'ug-saved__note', T('tradein.saved_note', { days: TN('tradein.saved_days', savedDays(api)) })));
    box.appendChild(resetBtn());
    return box;
  }

  /* One line for a new estimate, only once the device has kept it. */
  function keptLine(value) {
    var api = savedApi();
    if (!api || (savedFrom && savedFrom.sig === answersSig(S))) return null;
    var rec = null;
    try { rec = api.savedEstimate(); } catch (e) { rec = null; }
    if (!rec || rec.value !== value || answersSig(rec) !== answersSig(S)) return null;
    return h('p', 'ug-note ug-saved__kept', T('tradein.kept_line', { days: TN('tradein.saved_days', savedDays(api)) }));
  }

  var COND_LABELS = {
    flawless: { t: T('tradein.cond_flawless_title'), d: T('tradein.cond_flawless_desc') },
    good: { t: T('tradein.cond_good_title'), d: T('tradein.cond_good_desc') },
    fair: { t: T('tradein.cond_fair_title'), d: T('tradein.cond_fair_desc') },
    // "or a fault" was removed: the fault is now its own question, as it is in
    // the sell journey, and leaving it here too invited the customer to count
    // the same problem twice.
    damaged: { t: T('tradein.cond_damaged_title'), d: T('tradein.cond_damaged_desc') }
  };
  /* The four battery answers, 1 Oct 2026: the counter's own battery bands.
     Keys are the published offer axis (meta.offer_axes.battery). The
     September keys above90 / 85to90 / below85 are retired and never reused,
     so an answer given under those can never index the new table. `unsure`
     is the exception: it kept its key but changed meaning (it now takes the
     heavy battery amount, not the light one), and nothing in a stored record
     can tell the two apart. The deal storage-key bump (ug.deal.v1 -> v2 in
     assets/upgrade-deal.js, v1 never read) is its only guard. Insertion
     order is the button order. */
  /* Arabic phase 3: each label isolated on a right-to-left page, so the range
     "91–95%" keeps its order inside an Arabic button (unisolated it showed
     as "95%–91"). English: isolateText() returns the label untouched. */
  var BATT_LABELS = {
    '96plus': isolateText(T('tradein.batt_96plus')),
    '91to95': isolateText(T('tradein.batt_91to95')),
    '90orless': isolateText(T('tradein.batt_90orless')),
    unsure: isolateText(T('tradein.batt_unsure'))
  };

  function egp(n) {
    return new Intl.NumberFormat('en-EG', { maximumFractionDigits: 0 }).format(Math.round(n));
  }

  /* ---- the live sale price ------------------------------------------------
     THE SALE PRICE OF A DEVICE WE SELL COMES FROM SHOPIFY. Ruling, Omar
     Hussein, 22 September 2026. It is read from window.UpgradePrices, the
     server-rendered map of live variant prices mounted once in
     layout/theme.liquid (snippets/upgrade-price-map.liquid), keyed by full
     variant SKU — <device-code>-{RG|RP|RD}. This file no longer reads
     variant.list from assets/upgrade-tradein-data.json for any figure.

     What this file still reads from that JSON is unchanged and is not
     Shopify's: the precomputed offers table and everything meta.offer_*, the
     bonus rate and cap, and device identity. Those are trade-in valuation
     data, not sale prices.

     The bonus formula does not change — bonusFor(list) is still
     min(round(list x pct), cap). Only the `list` it is given is now live, so
     a bonus, a total credit and an estimated balance can no longer be
     computed off a price the checkout will not charge.

     null means "this site does not know what this costs right now" and must
     render as the hotline state, never as a blank, a zero or the last figure
     the surface happened to be holding — on cash on delivery a stale price is
     a courier asking for a different number than the page promised.

     `typeof window` rather than a bare `window`: tests/engines.test.js
     compiles this file with new Function('document', src) and no window at
     all, and a bare reference would throw there rather than read as "no price
     map on this page". */
  function priceApi() {
    return (typeof window !== 'undefined' && window.UpgradePrices) ? window.UpgradePrices : null;
  }

  function livePrice(code, grade) {
    var api = priceApi();
    if (!api || typeof api.getByCode !== 'function' || !code) return null;
    var p = api.getByCode(code, grade);
    return (typeof p === 'number' && isFinite(p) && p > 0) ? p : null;
  }

  /* One wording for a price this site cannot state, taken off the contract so
     it can never drift from the product page's own disabled state. */
  function noPriceLabel() {
    var api = priceApi();
    return (api && api.FALLBACK_LABEL) || T('tradein.call_to_order', { hotline: HOTLINE });
  }

  /* The variant SKU stem for a device/storage, from device identity in the
     JSON. Needed because a target pinned into the shared deal state by an
     older build may carry no code, and without a code there is no way to ask
     Shopify what it costs. */
  function codeFor(series, storage) {
    if (!DATA || !DATA.devices) return null;
    var dev = null;
    DATA.devices.forEach(function (d) { if (d.series === series) dev = d; });
    if (!dev) return null;
    var out = null;
    dev.variants.forEach(function (v) {
      if (!out && (!storage || v.storage === storage)) out = v.code;
    });
    return out;
  }

  function device() {
    if (!S.series) return null;
    for (var i = 0; i < DATA.devices.length; i++) {
      if (DATA.devices[i].series === S.series) return DATA.devices[i];
    }
    return null;
  }

  function variant() {
    var d = device();
    if (!d || !S.storage) return null;
    for (var i = 0; i < d.variants.length; i++) {
      if (d.variants[i].storage === S.storage) return d.variants[i];
    }
    return null;
  }

  /* ---- the lookup --------------------------------------------------------
     meta.offer_axes states the order of the three answer axes; the row is a
     flat array whose index is a mixed-radix combination of the three. The
     strides are DERIVED from the axis lengths rather than written here as 8
     and 2, for the same reason the bonus rate is not written here: one place
     to change one. meta.offer_index_formula publishes the same rule in words.

     Returns null — never a guessed index — when the axes are missing or an
     answer is not one the table knows about. The caller must read null as
     "cannot quote", never as zero. */
  function offerIndex() {
    var ax = DATA && DATA.meta && DATA.meta.offer_axes;
    if (!ax || !ax.condition || !ax.battery || !ax.fault) return -1;

    var ci = ax.condition.indexOf(S.condition);
    var bi = ax.battery.indexOf(S.battery);
    var fi = ax.fault.indexOf(!!S.faulty);
    if (ci < 0 || bi < 0 || fi < 0) return -1;

    return ci * (ax.battery.length * ax.fault.length) + bi * ax.fault.length + fi;
  }

  /* The rung this offer sits on, as the table states it. meta.offer_bands maps
     a band code to a rung label; a null label means payable but below the
     lowest named rung, and we do not invent a name for it. The refusal code is
     meta.offer_refused_band and is handled by the caller, not named here. */
  function bandLabel(code) {
    var map = DATA && DATA.meta && DATA.meta.offer_bands;
    if (!map || !Object.prototype.hasOwnProperty.call(map, code)) return null;
    return map[code] || null;
  }

  /* ---- the engine --------------------------------------------------------
     No arithmetic. One row, one slot, one figure.

     A missing row, an unreadable index or a slot the table marks refused all
     end the same way: refused === true and cash === null, so no figure can
     reach the screen. Refusal is checked on BOTH lanes the data publishes it
     on — a null cash value and the refusal band code — because either one
     alone would be enough and neither is allowed to be the only guard. */
  function quote() {
    var d = device(), v = variant();
    if (!d || !v || !DATA.offers) return null;

    var row = DATA.offers[v.code];
    var i = offerIndex();
    if (!row || !row.c || i < 0 || i >= row.c.length) {
      return { cash: null, band: null, refused: true, variant: v, device: d };
    }

    var cash = row.c[i];
    var code = typeof row.b === 'string' ? row.b.charAt(i) : '';
    var refusedCode = DATA.meta && DATA.meta.offer_refused_band;

    if (cash === null || cash === undefined || code === refusedCode) {
      return { cash: null, band: null, refused: true, variant: v, device: d };
    }

    return {
      cash: cash,
      band: { key: code, label: bandLabel(code) },
      refused: false,
      variant: v,
      device: d
    };
  }

  /* What the customer told us, in the words they were shown — the panel below
     states what was priced, so it has to read back their own answers and not
     the keys behind them. The battery line is phrased rather than pasted:
     BATT_LABELS is written for buttons ("Don't know"), which does not read as
     a sentence. */
  function pricedSummary(q) {
    var cond = COND_LABELS[S.condition] ? COND_LABELS[S.condition].t : null;
    var batt = {
      '96plus': T('tradein.priced_batt_96plus'),
      '91to95': T('tradein.priced_batt_91to95'),
      '90orless': T('tradein.priced_batt_90orless'),
      unsure: T('tradein.priced_batt_unsure')
    }[S.battery] || null;

    return [
      isolateText(q.device.label + ' ' + q.variant.storage),
      cond ? T('tradein.priced_condition', { condition: cond }) : null,
      batt,
      S.faulty ? T('tradein.priced_fault_yes') : T('tradein.priced_fault_no')
    ].filter(Boolean).join(T('tradein.list_separator'));
  }

  /* Ruling R3 — the rate and the cap live in exactly one place, the meta block
     of assets/upgrade-tradein-data.json, and are not restated here in code or
     in prose. Field preference matches assets/upgrade-deal.js and
     assets/upgrade-sell.js exactly: bonus_pct / bonus_cap, falling back to
     discount_pct / discount_cap. See the header note on why the three must not
     drift apart.

     Unreadable means we cannot state a bonus, so we state none rather than a
     guessed one. */
  function bonusRate() {
    var m = DATA && DATA.meta;
    if (!m) return null;
    var pct = (typeof m.bonus_pct === 'number') ? m.bonus_pct : m.discount_pct;
    var cap = (typeof m.bonus_cap === 'number') ? m.bonus_cap : m.discount_cap;
    if (typeof pct !== 'number' || !isFinite(pct) || pct <= 0 || pct > 1) return null;
    if (typeof cap !== 'number' || !isFinite(cap) || cap <= 0) return null;
    return { pct: pct, cap: cap };
  }

  /* Bonus = the website discount, redirected. Costs nothing extra.

     Returns null — not a number — when the rate or the price is unusable, and
     callers must read null as "cannot be shown", never as zero. Without this
     guard a bad data deploy reached the customer as "NaN EGP" printed directly
     beside the word guaranteed.

     SINGLE IMPLEMENTATION, 29 Sep 2026 (LOG-029). The offer-aware rate
     resolution (an active offer's OWN bonus rate/cap for a SKU it covers,
     falling back to the standing rate) lives in exactly one place --
     assets/upgrade-deal.js's bonusFor() -- so this file and
     assets/upgrade-sell.js cannot drift onto a different answer for the
     same device. When window.UpgradeDeal is present (every real page: the
     deal bar shares this screen with every trade-up surface) this function
     delegates to it, passing `code` through so the offer check can name the
     exact SKU. `code` is the device code the caller already has (v.code /
     u.code); `cardSku()` below turns it into the full SKU bonusFor() needs.

     The local computation just below (bonusRate() over DATA.meta only) is
     kept as the FALLBACK for when UpgradeDeal is not on the page -- today
     that is only this file's own isolated test harness
     (tests/tradeup-balance.test.js, tests/engines.test.js), never a real
     storefront route. It intentionally does NOT apply an offer rate: a
     surface running without UpgradeDeal has no way to check whether an
     offer is even live, so the safe answer is the standing rate, exactly
     what this function always returned before LOG-029. */
  function cardSku(code) {
    if (!code) return null;
    var api = priceApi();
    if (!api || typeof api.skuFor !== 'function') return null;
    try { return api.skuFor(code, CARD_GRADE) || null; } catch (e) { return null; }
  }

  function bonusFor(listPrice, code) {
    // 29 Sep 2026, review fix (should-fix, finding 20): the price guard used
    // to run only on the FALLBACK path below. window.UpgradeDeal.bonusFor()
    // does `(price || 0) * r.pct` internally -- correct for a real device
    // price, but it turns an unusable price into a real number rather than
    // null: NaN/undefined/null all became "0 EGP, guaranteed" instead of no
    // bonus shown at all (the exact "NaN EGP printed beside guaranteed"
    // failure this function's own comment already warns against), and a
    // negative price produced a negative bonus.
    if (typeof listPrice !== 'number' || !isFinite(listPrice) || listPrice <= 0) return null;
    var dealApi = (typeof window !== 'undefined') ? window.UpgradeDeal : null;
    if (dealApi && typeof dealApi.bonusFor === 'function') {
      return dealApi.bonusFor(listPrice, cardSku(code));
    }
    var r = bonusRate();
    if (!r) return null;
    return Math.min(Math.round(listPrice * r.pct), r.cap);
  }

  /* Devices to trade up to: same brand, priced above what they're handing in.

     quote() returns null when it cannot quote at all and a refused quote whose
     cash is null when the table declines the combination. Neither can seed a
     rail — there is no valuation to price options against — so both return an
     empty list here rather than throwing on q.device or comparing against a
     null cash figure, which would let every option through. */
  /* THE GENERATION OF A DEVICE, read off its own series code.

     A TRADE-UP GOES UP IN GENERATIONS. STRICTLY. Omar, 24 September 2026:
     "i'd prefer not forget it go up in generations, 14 to 15 15 pro max or 16
     then if non browse for their replacement".

     So selling a 14 offers a 15, a 15 Pro Max or a 16 -- never a 13, and never
     another 14 in any tier. When nothing newer is in range the rail shows
     nothing and the customer is sent to browse instead; see stepResult(), which
     has a branch for exactly that and is the normal state for the newest
     devices in the catalogue.

     NOTE FOR WHOEVER READS THIS NEXT: same-generation was allowed for about
     twenty minutes on 24 September ("same generation is fine, allow 14 pro max
     if selling a 14") and then withdrawn in favour of the rule above. It is
     `>` on purpose. Do not relax it to `>=` without asking Omar.

     The number is taken from the series code, which is the catalogue's own
     identifier and is not invented here: IPHONE13 / IPHONE15PROMAX / S24ULTRA
     all carry their generation as the first run of digits. Verified against all
     27 devices in assets/upgrade-tradein-data.json -- every one parses.

     There is no cross-brand comparison to make, and deliberately so: the caller
     has already required d.brand === q.device.brand, so 14 is only ever weighed
     against another iPhone and 24 against another Galaxy. Comparing an iPhone 14
     with a Galaxy S23 would need real release dates, which this file does not
     hold and must not guess at. */
  function generationOf(series) {
    var m = /(\d+)/.exec(series || '');
    return m ? parseInt(m[1], 10) : null;
  }

  function isNewerGeneration(targetSeries, sourceSeries) {
    var t = generationOf(targetSeries);
    var s = generationOf(sourceSeries);
    /* Unknown means NOT offered. If a future series code carries no number, the
       honest answer is that we cannot show it is newer, and the rule is a
       promise to the customer rather than a preference. Warn, because the fix
       is to give the device a parseable code, not to relax this. */
    if (t === null || s === null) {
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('[upgrade-tradein] cannot read a generation from "' +
          (t === null ? targetSeries : sourceSeries) + '", so it is not offered as a trade-up. ' +
          'Series codes must carry their generation as digits (IPHONE15PROMAX, S24ULTRA).');
      }
      return false;
    }
    return t > s;
  }

  /* ---- the model list's order (Lynkrs Q7 / D3, batch 2, W1a, 6 Oct 2026) --
     Newest first, grouped by family: the rule batch 2 set for this tool, the
     sell tool (assets/upgrade-sell.js, W1b, the same four functions) and the
     shop. Apple, then Samsung; within a brand the generation descending
     (iPhone 17 > 16 > 15 > 14 > 13; Galaxy S25 > S24 > S23); within a
     generation the top model first (Ultra or Pro Max, then Pro, then Plus,
     then the base model). Read from each device's own brand and series code
     (generationOf() above), so a phone added to the data file takes its place
     without an edit here; the file itself is never reordered (run_all.py
     checksums it). The buttons used to follow the file: iPhone 13, 14, 15,
     16, 15 Pro Max, 13 Pro... Display only: the offer, the bonus and the
     trade-up cards do not depend on it. */
  var BRAND_ORDER = ['Apple', 'Samsung'];

  function tierOf(series) {
    var rest = String(series || '').toUpperCase().replace(/^[A-Z]*\d+/, '');
    if (rest === 'ULTRA' || rest === 'PROMAX') return 0;
    if (rest === 'PRO') return 1;
    if (rest === 'PLUS') return 2;
    if (rest === '') return 3;
    return 4;   // a model name this list does not know goes after the known ones
  }

  function modelOrder(a, b) {
    var ba = BRAND_ORDER.indexOf(a.brand), bb = BRAND_ORDER.indexOf(b.brand);
    if (ba === -1) ba = BRAND_ORDER.length;
    if (bb === -1) bb = BRAND_ORDER.length;
    if (ba !== bb) return ba - bb;
    var ga = generationOf(a.series), gb = generationOf(b.series);
    if (ga !== gb) return (gb === null ? -1 : gb) - (ga === null ? -1 : ga);
    return tierOf(a.series) - tierOf(b.series);
  }

  // Stable in every browser: two devices the rule cannot tell apart keep the file's order.
  function sortModels(list) {
    return list.map(function (d, i) { return { d: d, i: i }; })
      .sort(function (x, y) { return modelOrder(x.d, y.d) || (x.i - y.i); })
      .map(function (x) { return x.d; });
  }

  /* TRADE-UP IN STOCK, 11 Oct 2026 (2026-10-11_tradeup-in-stock/CHANGES.md).
     Omar: "when someone tries to trade-in it shows phones that are out of
     stock for the suggest i would like it to show whats in stock". A card is
     offered only when the exact phone it offers is in stock in Shopify: this
     series and storage (the variant's code), the card's grade (CARD_GRADE),
     in any colour. The fact is Shopify's own yes/no, read through
     window.UpgradeFacts.inStock(<code>-<RX>) (snippets/upgrade-catalogue-
     facts.liquid: true when ANY colour of that grade is available). UNKNOWN
     IS NOT IN STOCK: no UpgradeFacts, no stock fact on this page, or no row
     for the phone -- the card is not offered, and the console says so once.
     stockHidden counts the cards one tradeUpOptions() call left out this way,
     so stepResult() can tell "nothing newer in range" (its own sentence)
     from "nothing newer in stock" (no rail and no heading at all). Nothing
     here reads or changes a price, a bonus or a balance. */
  var stockHidden = 0;
  var stockWarned = false;
  // The grade key's suffix, as window.UpgradePrices.SUFFIX names it: <CODE>-<RX> is the key the facts are kept by.
  var STOCK_SUFFIX = { g: 'RG', p: 'RP', d: 'RD' };

  function cardInStock(code) {
    var facts = (typeof window !== 'undefined') ? window.UpgradeFacts : null;
    var sku = code ? String(code).trim().toUpperCase() + '-' + STOCK_SUFFIX[CARD_GRADE] : null;
    var known = null;
    if (sku && facts && typeof facts.inStock === 'function') {
      try { known = facts.inStock(sku); } catch (e) { known = null; }
    }
    if (known === true) return true;
    if (known !== false && !stockWarned && typeof console !== 'undefined' && typeof console.warn === 'function') {
      stockWarned = true;
      console.warn('[upgrade-tradein] no stock fact for ' + (sku || code) + ' (window.UpgradeFacts.inStock), so it is ' +
        'not offered as a trade-up: a card is shown only for a phone Shopify says is in stock.');
    }
    return false;
  }

  function tradeUpOptions(q) {
    var out = [];
    stockHidden = 0;   // TRADE-UP IN STOCK 11 OCT 2026: this call's count (see cardInStock above)
    if (!q || !q.device || typeof q.cash !== 'number') return out;
    DATA.devices.forEach(function (d) {
      if (d.brand !== q.device.brand) return;
      if (d.series === q.device.series) return; // never offer the device they're handing in
      // Up in generations, strictly -- see isNewerGeneration above.
      if (!isNewerGeneration(d.series, q.device.series)) return;
      d.variants.forEach(function (v) {
        /* The card's price, its bonus and its estimated balance are all
           computed from this one figure, so it is the LIVE Platinum price of
           this exact variant. A variant Shopify has no price for is not
           offered as a card at all: a card states a guaranteed bonus and a
           balance, and neither can be stated against a price we do not know.
           The route out stays open — see the bridge in stepResult(). */
        var list = livePrice(v.code, CARD_GRADE);
        if (list === null) return;

        /* THE MARGIN IS MEASURED AGAINST THE CREDIT, NOT AGAINST THE PHONE.
           Fixed 23 September 2026. It used to read `list > q.cash * 1.05`,
           which left 5% of headroom over the customer's phone and then added
           the trade-up bonus — up to 4,000 EGP — on top of it. Any target in
           the window cash*1.05 < list < cash + bonus was therefore offered as
           a card whose credit was larger than its own price, and the negative
           balance was clamped to zero further down and printed as "Estimated
           balance 0 EGP". Measured on the shipped code before this change:
           2,380 of 5,263 cards (45.2%) said zero, and in 394 outcomes every
           card on the rail did. The window is non-empty for every phone in the
           catalogue, so this was structural, not an edge case.
           A card states a guaranteed bonus and a balance. If the credit
           already covers the device, there is no balance to state and the
           card must not be built. */
        var bonus = bonusFor(list, v.code);
        if (bonus === null) return;
        if (list > (q.cash + bonus) * 1.05 && list < q.cash * 2.6) {
          /* TRADE-UP IN STOCK, 11 Oct 2026: only a phone in stock (cardInStock
             above). Left out HERE, before the sort and the three-card cut, so
             the rail takes the next in-stock phone rather than showing fewer. */
          if (!cardInStock(v.code)) { stockHidden++; return; }
          out.push({
            label: d.label, storage: v.storage, list: list,
            listG: livePrice(v.code, 'g'), series: d.series, code: v.code
          });
        }
      });
    });
    out.sort(function (a, b) { return a.list - b.list; });
    var seen = {}, uniq = [];
    for (var i = 0; i < out.length && uniq.length < 3; i++) {
      if (seen[out[i].label]) continue;
      seen[out[i].label] = 1; uniq.push(out[i]);
    }
    return uniq;
  }

  /* Terminal action of a trade-up card, and the last thing that happens in the
     whole journey — so it is the last chance to write down what the customer
     chose. It used to write nothing: the device was never pinned, and the link
     carried a ?credit= copy of a number that already lived in the shared
     state. Two copies of one credit is how two different totals end up on one
     screen, so the copy is gone and the state is the single source.

     What is pinned is the exact variant the card was priced on — the Platinum
     price of the storage the card names — so the page the customer lands on
     computes the same balance they just clicked, not a balance for
     variants[0]. */
  function pinTradeUpTarget(u) {
    var dev = null;
    DATA.devices.forEach(function (d) { if (d.series === u.series) dev = d; });
    var v = null;
    if (dev) dev.variants.forEach(function (x) { if (x.storage === u.storage) v = x; });

    var base = ugAssetBase();
    var target = {
      series: u.series,
      label: u.label,
      image: (dev && dev.image_cut && base) ? (base + dev.image_cut) : (dev ? dev.image : null),
      grade: CARD_GRADE,
      gradeLabel: GRADE_LABELS[CARD_GRADE],
      storage: u.storage,
      code: u.code || (v ? v.code : null),
      // The live Platinum price this card was built on. tradeUpOptions() has
      // already proved it is a real number — a card is never built without
      // one — so nothing null can reach UpgradeDeal.totals(), which reads
      // `target.price || 0` and would otherwise quote a 0 EGP phone.
      price: u.list
    };
    if (window.UpgradeDeal) window.UpgradeDeal.setTarget(target);
    return target;
  }

  /* mineName / value / bonus rather than one pre-summed total: the confirmed
     card is the last thing the customer reads before leaving this page, and
     calling the whole credit "estimated" here — as it used to — told them the
     bonus might shrink at the inspection. It cannot. */
  function showCreditApplied(cardEl, u, mineName, value, bonus) {
    pinTradeUpTarget(u);
    /* 28 Sep 2026 (LOG-021 review): the summary drawn above for a phone pinned
       earlier now describes the wrong phone, and its order button would order
       it. The customer's choice is this card; the bar and this card's link
       carry it from here. */
    var staleSum = document.getElementById('ug-tradein-summary');
    if (staleSum && staleSum.parentNode) staleSum.parentNode.removeChild(staleSum);

    /* This is the one thing on the page that render() did not draw and cannot
       redraw: the card is rewritten in place, and the customer's choice is
       recorded nowhere else. A blind repaint would wipe it back to the rail of
       options they just chose from. The flag is cleared at the top of
       render(), so it only ever describes a DOM in this confirmed state. */
    creditApplied = true;

    cardEl.innerHTML = '';
    cardEl.setAttribute('role', 'status');
    cardEl.appendChild(h('span', 'ug-up__grade', gradeName(CARD_GRADE)));
    cardEl.appendChild(h('span', 'ug-up__name', u.label + ' ' + u.storage));
    var br = h('div', 'ug-up__calc');
    br.innerHTML =
      '<span>' + T('tradein.your_phone_estimate', { phone: mineName }) + '</span><b>' + fig(value) + '</b>' +
      '<span>' + T('tradein.bonus_guaranteed') + '</span><b>' + plusFig(bonus) + '</b>';
    cardEl.appendChild(br);
    cardEl.appendChild(h('span', 'ug-up__pay', T('tradein.total_credit_amount', { amount: fig(value + bonus) })));
    // 29 Sep 2026 (LOG-028, review fix, dated per finding 10): ", plus
    // delivery" dropped from the end of this note -- delivery is free, its
    // cost is already in the phone's price.
    cardEl.appendChild(h('p', 'ug-note', T('tradein.credit_applied_note')));
    var view = document.createElement('a');
    view.className = 'ug-cta';
    view.style.display = 'inline-block';
    view.style.textAlign = 'center';
    view.style.textDecoration = 'none';
    view.textContent = TX('viewLabel');
    // Device, storage AND grade, so the destination restores the exact variant
    // this card was priced on. No credit parameter — the shared state holds it.
    // Lynkrs B6 (6 Oct 2026, T3): the phone's own product page,
    // /products/<handle>, from window.UpgradeRoutes (the shop if Shopify has
    // no product for it). It used to be /pages/device?device=<series>.
    // Arabic port (pA): the address keeps the language prefix (localHref()).
    var routes = window.UpgradeRoutes;
    view.href = (routes && typeof routes.deviceHref === 'function')
      ? localHref(routes.deviceHref(u.series, { grade: CARD_GRADE, storage: u.storage }))
      : routeRoot() + 'pages/shop';
    cardEl.appendChild(view);

    /* Lynkrs PG-TradeIn-4 (6 Oct 2026, T2). The card above grows by about
       184px when it is rewritten, and the deal bar appears at the foot of the
       window at the same moment, so a "Choose this" tapped low on a phone left
       "View this device" under the bar, where a tap lands on the bar instead.
       The page now brings the link into view ABOVE the bar (reveal(), and its
       scroll-margin in upgrade-tradein.css), only when it is not already
       there, and without gliding for anyone who asked for reduced motion.
       Focus moves to it too: the "Choose this" button that had focus has just
       been removed with the old card, which left a keyboard user on <body>. */
    try { view.focus({ preventScroll: true }); } catch (e) { /* no focus() here: nothing to move */ }
    reveal(view);
  }

  /* ---- moving the page for the customer (Lynkrs T2, 6 Oct 2026) ----------
     Q21. Every answer used to smooth-scroll the active step to the MIDDLE of
     the window, whether or not it was already in view. On a phone a tap that
     lands while the page is still gliding can hit whatever has slid under the
     finger, or only stop the glide: the likely cause of "the first tap only
     scrolls" (not reproduced in headless Chrome with touch, 6 Oct 2026; a
     real-phone check is still owed). Now the page moves only when the step
     is NOT fully in view between the sticky header and the deal bar; it
     moves as little as it can (a step taller than the window is brought to
     its top: "center" left the result of the last answer -- 2,734px tall at
     375px wide, measured offline on 6 Oct 2026 -- with its "Your offer"
     heading about 880px ABOVE the screen, the customer landing half-way down
     the trade-up cards); and it jumps instead of gliding for anyone whose
     device asks for reduced motion. PG-TradeIn-4 uses the same rule for
     "View this device". */
  function reducedMotion() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) { return false; }
  }

  /* The part of the window the customer can actually see: below the sticky
     header -- the element's own scroll-margin-top, which upgrade-tradein.css
     sets to the header's height plus a gap -- and above the deal bar, whose
     measured height upgrade-deal.js publishes on <html> as --ug-deal-h. */
  function visibleBand(el) {
    var band = { top: 0, bottom: window.innerHeight || 0 };
    try {
      band.top = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
      band.bottom -= parseFloat(document.documentElement.style.getPropertyValue('--ug-deal-h')) || 0;
    } catch (e) { /* no layout here: the whole window */ }
    return band;
  }

  function fullyInView(el) {
    if (!el || typeof el.getBoundingClientRect !== 'function' || !window.innerHeight) return false;
    var r = el.getBoundingClientRect();
    var band = visibleBand(el);
    return r.top >= band.top && r.bottom <= band.bottom;
  }

  /* true when it moved the page. */
  function reveal(el) {
    if (!el || typeof el.scrollIntoView !== 'function' || fullyInView(el)) return false;
    var band = visibleBand(el);
    var tall = el.getBoundingClientRect().height > (band.bottom - band.top);
    el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: tall ? 'start' : 'nearest' });
    return true;
  }

  /* Q21, the other half. aria-pressed (LF11, 29 Sep) was set on every answer,
     but render() rebuilds the whole tool, so the button the customer had just
     pressed was removed and focus fell to <body> (measured in Chrome, 6 Oct
     2026): a screen reader never heard "pressed" on the answer just given.
     focusKey() notes which answer had focus (its class, its words and its
     step) before the rebuild; restoreFocus() puts focus on the same answer
     afterwards, without scrolling. Nothing happens when focus was elsewhere. */
  function focusKey() {
    var a = document.activeElement;
    if (!a || a.tagName !== 'BUTTON' || typeof root.contains !== 'function' || !root.contains(a)) return null;
    var steps = root.querySelectorAll('.ug-step');
    var step = a.closest ? a.closest('.ug-step') : null;
    return { cls: String(a.className).split(' ')[0], text: a.textContent, step: step ? Array.prototype.indexOf.call(steps, step) : -1 };
  }

  function restoreFocus(k) {
    if (!k || !k.cls) return;
    var scope = k.step >= 0 ? root.querySelectorAll('.ug-step')[k.step] : root;
    if (!scope) return;
    var list = scope.querySelectorAll('button.' + k.cls);
    for (var i = 0; i < list.length; i++) {
      if (list[i].textContent === k.text) {
        try { list[i].focus({ preventScroll: true }); } catch (e) { /* nothing to restore to */ }
        return;
      }
    }
  }

  /* ---- rendering --------------------------------------------------------- */
  function h(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  /* One summary row, same markup and same classes the booking form builds in
     sections/upgrade-book.liquid, so the two summaries that render on
     /pages/trade-in line up instead of each inventing their own row. */
  function bookRow(k, v, cls) {
    return '<div class="ug-book__row' + (cls ? ' ' + cls : '') + '">' +
      '<span>' + k + '</span><span>' + v + '</span></div>';
  }

  /* The phone they came here to buy, if they arrived from a product page.
     Pinning it keeps the goal visible while they answer, and stops the page
     asking them to choose a phone they have already chosen. */
  /* A target we could not price, kept for display only so the "trading up to"
     card does not blink out of the flow mid-quote. It is deliberately NOT in
     the shared deal state: see unpin() below. */
  var unpricedTarget = null;

  /* True while the trade-up rail has been replaced in place by the "credit
     applied" confirmation (showCreditApplied below). Set there, cleared by
     render(). */
  var creditApplied = false;

  /* A target with no live price must not stay pinned. UpgradeDeal.totals()
     reads `target.price || 0`, so a null price there becomes a 0 EGP phone, a
     0 bonus and a 0 balance on the deal bar and in the booking summary — the
     exact zero the contract forbids. Dropping it takes the bar down instead,
     and the card below says to call. */
  function unpin(t) {
    if (window.UpgradeDeal) window.UpgradeDeal.clearTarget();
    unpricedTarget = t;
    return t;
  }

  /* Colour variants, ported 27 Sep 2026. A target pinned by the product page
     may carry target.colour -- the Shopify Colour option value -- and
     target.sku, the colour variant's SKU that the booking form reads the
     staff-facing English colour name from. Price does not vary by colour, so
     nothing below reads either of them; but every place that REBUILDS a target
     must carry them across, or the customer's colour silently falls off the
     deal on a re-price before the booking form ever names it. A target without
     a colour gets no new keys, so it is exactly the object it always was. */
  function keepColour(from, to) {
    if (from && typeof from.colour === 'string' && from.colour.trim()) {
      to.colour = from.colour;
      if (typeof from.sku === 'string' && from.sku) to.sku = from.sku;
    }
    /* 28 Sep 2026 (LOG-021): the same goes for the Shopify variant the product
       page pinned (variantId) and its page (url). A re-price changes the
       figure, not which phone it is, and without them the "Order with
       trade-in" button would fall back to a link to the phone's page
       ("View <phone>" on the bar since Lynkrs T2, 6 Oct 2026). */
    if (from && from.variantId && from.series === to.series && from.storage === to.storage &&
        (from.grade || CARD_GRADE) === to.grade) {
      to.variantId = from.variantId;
    }
    if (from && typeof from.url === 'string' && from.url) to.url = from.url;
    return to;
  }

  /* "iPhone 15 · Platinum · 128GB", plus " · Blue" when the target carries a
     colour -- the same order the deal bar and the booking form use. Both
     callers put this into innerHTML, so the colour (a Shopify option value,
     not this theme's own data) is escaped; the other three parts are exactly
     what they always were. */
  function targetLabel(t) {
    // Every part escaped (28 Sep audit): they come from stored session state,
    // and the colour is a Shopify option value, not this theme's own data.
    var esc = function (v) {
      return String(v == null ? '' : v).trim()
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    };
    var colour = (typeof t.colour === 'string') ? esc(t.colour) : '';
    /* Arabic phase 3: on a translated page the grade is named from its key
       (t.grade), not from the English gradeLabel the state stores, and the
       Latin parts (model, storage, colour: LOG-039) are isolated so the
       list reads in order in right-to-left text. English: unchanged. */
    if (I18N && (t.grade === 'g' || t.grade === 'p' || t.grade === 'd')) {
      return [isolateText(esc(t.label)), esc(gradeName(t.grade)), isolateText(esc(t.storage)), colour ? isolateText(colour) : '']
        .filter(Boolean).join(' · ');
    }
    return [esc(t.label), esc(t.gradeLabel), esc(t.storage), colour].filter(Boolean).join(' · ');
  }


  function targetOf() {
    var deal = window.UpgradeDeal && window.UpgradeDeal.get();
    if (deal && deal.target) {
      var pinned = deal.target;
      /* Re-priced against Shopify on every render, not trusted as stored. The
         deal state is persisted, so a target pinned before a price change
         would otherwise carry the old figure into the bonus and the balance.
         The code is re-derived from device identity when an older stored
         target carries none. */
      var pinnedGrade = pinned.grade || CARD_GRADE;
      var pinnedCode = pinned.code || codeFor(pinned.series, pinned.storage);
      var pinnedPrice = livePrice(pinnedCode, pinnedGrade);
      // Colour variants, ported 27 Sep 2026: both rebuilds keep colour and SKU.
      if (pinnedPrice === null) {
        return unpin(keepColour(pinned, {
          series: pinned.series, label: pinned.label, image: pinned.image,
          grade: pinnedGrade, gradeLabel: pinned.gradeLabel || GRADE_LABELS[pinnedGrade] || '',
          storage: pinned.storage, code: pinnedCode, price: null
        }));
      }
      unpricedTarget = null;
      if (pinned.price !== pinnedPrice || pinned.code !== pinnedCode) {
        pinned = keepColour(pinned, {
          series: pinned.series, label: pinned.label, image: pinned.image,
          grade: pinnedGrade, gradeLabel: pinned.gradeLabel || GRADE_LABELS[pinnedGrade] || '',
          storage: pinned.storage, code: pinnedCode, price: pinnedPrice
        });
        if (window.UpgradeDeal) window.UpgradeDeal.setTarget(pinned);
      }
      return pinned;
    }
    /* A target we could not price on an earlier render. RE-ASK before reusing
       it: the price map's Liquid seed covers at most 50 products and the walk
       that fills in the rest settles after the first paint, so "no price" at
       first paint is not a verdict. Without this re-ask the branch is a
       one-way door -- unpin() has already cleared the shared deal state, so
       the branch above no longer fires, and this cached copy would hold the
       card on the hotline state for the life of the page even once Shopify's
       price for that exact variant is in the map.

       Recovering it re-pins it, which brings the deal bar and the booking
       summary back with the same figure the card now prints. Still null means
       still unpriced, and the hotline state stands -- never a blank, never 0,
       never a figure from somewhere else. */
    if (unpricedTarget) {
      var againGrade = unpricedTarget.grade || CARD_GRADE;
      var againCode = unpricedTarget.code || codeFor(unpricedTarget.series, unpricedTarget.storage);
      var againPrice = livePrice(againCode, againGrade);
      if (againPrice === null) return unpricedTarget;
      // Colour variants, ported 27 Sep 2026: the re-pin keeps colour and SKU.
      var repriced = keepColour(unpricedTarget, {
        series: unpricedTarget.series, label: unpricedTarget.label, image: unpricedTarget.image,
        grade: againGrade, gradeLabel: unpricedTarget.gradeLabel || GRADE_LABELS[againGrade] || '',
        storage: unpricedTarget.storage, code: againCode, price: againPrice
      });
      unpricedTarget = null;
      if (window.UpgradeDeal) window.UpgradeDeal.setTarget(repriced);
      return repriced;
    }
    var p = new URLSearchParams(location.search);
    var series = p.get('target');
    if (!series || !DATA) return null;
    var dev = null;
    DATA.devices.forEach(function (d) { if (d.series === series) dev = d; });
    if (!dev) return null;
    var storage = p.get('targetStorage');
    /* Normalised through the price contract's own grade whitelist, so a link
       that says Gold or RG still labels and prices as 'g'. An unknown value
       survives unchanged, prices as null and lands on the hotline state
       rather than indexing into whatever happens to be there. */
    var gradeParam = p.get('targetGrade') || CARD_GRADE;
    var gradeApi = priceApi();
    var grade = (gradeApi && typeof gradeApi.gradeKey === 'function' && gradeApi.gradeKey(gradeParam)) || gradeParam;
    var v = null;
    dev.variants.forEach(function (x) { if (!v && (!storage || x.storage === storage)) v = x; });
    if (!v) return null;
    var t = {
      series: dev.series, label: dev.label,
      // Use the transparent cut-out so the pinned target is a lit device on
      // the dark stage, not a white square. Falls back to the original.
      image: (dev.image_cut && ugAssetBase()) ? (ugAssetBase() + dev.image_cut) : dev.image,
      grade: grade, gradeLabel: GRADE_LABELS[grade] || '',
      // The live price of this exact variant and grade, from Shopify.
      storage: v.storage, code: v.code, price: livePrice(v.code, grade)
    };
    if (t.price === null) return unpin(t);
    if (window.UpgradeDeal) window.UpgradeDeal.setTarget(t);
    return t;
  }

  /* LF14 fix (29 Sep 2026, fix_now, audit w4k72rk5w). targetOf() (above)
     checks the SESSION pin (window.UpgradeDeal.get().target) before it
     ever reads the URL -- correct for the common case (choosing a phone
     on the PDP, then pressing Back to this page should keep that choice),
     but it meant a shared or bookmarked ?target= link opened on an OLDER
     pin left over in the same tab, never the phone the link named.

     Called once at boot, before the first render. Only overrides the pin
     on a genuinely FRESH navigation -- performance.getEntriesByType(
     'navigation')[0].type === 'navigate', MDN's own name for "not
     back_forward, not reload, not prerender" -- and only when the URL
     names a real, PRICEABLE device that differs from whatever is already
     pinned; an unpriceable URL target changes nothing (today's pin
     stands), and back/forward or a reload never touches the pin at all,
     because that is the customer's own latest choice on the page they
     are returning to, not something the URL should override. The
     valuation itself (S, restoreFromLiveTradein) is untouched either
     way -- this only ever re-pins the TARGET. */
  function reconcileUrlTarget() {
    if (!window.UpgradeDeal || typeof window.UpgradeDeal.get !== 'function') { return; }
    var navType = null;
    try {
      var entries = (typeof performance !== 'undefined' && performance.getEntriesByType)
        ? performance.getEntriesByType('navigation') : null;
      navType = (entries && entries[0]) ? entries[0].type : null;
    } catch (e) { navType = null; }
    if (navType !== 'navigate') { return; }

    var p = new URLSearchParams(location.search);
    var series = p.get('target');
    if (!series || !DATA) { return; }
    var dev = null;
    DATA.devices.forEach(function (d) { if (d.series === series) dev = d; });
    if (!dev) { return; }
    var storage = p.get('targetStorage');
    var gradeParam = p.get('targetGrade') || CARD_GRADE;
    var gradeApi = priceApi();
    var grade = (gradeApi && typeof gradeApi.gradeKey === 'function' && gradeApi.gradeKey(gradeParam)) || gradeParam;
    var v = null;
    dev.variants.forEach(function (x) { if (!v && (!storage || x.storage === storage)) v = x; });
    if (!v) { return; }
    var price = livePrice(v.code, grade);
    if (price === null) { return; }

    var current = window.UpgradeDeal.get().target;
    var samePin = current && current.series === dev.series &&
      (current.grade || CARD_GRADE) === grade && current.storage === v.storage;
    if (samePin) { return; }

    window.UpgradeDeal.setTarget({
      series: dev.series, label: dev.label,
      image: (dev.image_cut && ugAssetBase()) ? (ugAssetBase() + dev.image_cut) : dev.image,
      grade: grade, gradeLabel: GRADE_LABELS[grade] || '',
      storage: v.storage, code: v.code, price: price
    });
  }

  /* The WebP twin of a theme cut-out, or null. Only assets/ugdev-<name>.png
     has one (made beside every PNG, batch 2); the version query is dropped
     because it belongs to the PNG. */
  function webpTwin(src) {
    var m = /^([^?#]*\/)?(ugdev-[a-z0-9-]+)\.png(?:[?#].*)?$/i.exec(String(src || ''));
    return m ? (m[1] || '') + m[2] + '.webp' : null;
  }

  function targetCard(t) {
    var w = h('div', 'ug-target');
    var media = h('div', 'ug-target__media');
    if (t.image) {
      var im = document.createElement('img');
      im.src = t.image; im.alt = t.label; im.width = 58; im.height = 58; im.loading = 'lazy';
      /* Lynkrs SEO-Images / decision D4 (batch 2, W1a): a theme cut-out
         (assets/ugdev-*.png) is offered as its WebP twin first, the PNG kept
         as the fallback and as the <img> every rule styles. Any other picture
         (a Shopify product image pinned by the product page) is as before. */
      im.decoding = 'async';
      var webp = webpTwin(t.image);
      if (webp) {
        var pic = document.createElement('picture');
        var source = document.createElement('source');
        source.type = 'image/webp';
        source.srcset = webp;
        pic.appendChild(source);
        pic.appendChild(im);
        media.appendChild(pic);
      } else {
        media.appendChild(im);
      }
    }
    w.appendChild(media);
    var b = h('div', 'ug-target__body');
    b.appendChild(h('p', 'ug-target__eyebrow', TXH('targetEyebrow')));
    // Colour variants, ported 27 Sep 2026: targetLabel() adds the colour, escaped.
    b.appendChild(h('p', 'ug-target__name', targetLabel(t)));
    /* No live price, no figure. egp(null) would print "0" here and the target
       has already been unpinned, so the summary below it is suppressed too —
       the customer is told to call rather than shown a balance built on a
       price this site cannot stand behind. */
    b.appendChild(h('p', 'ug-target__price',
      (typeof t.price === 'number' && isFinite(t.price) && t.price > 0)
        ? money(t.price)
        : noPriceLabel()));
    w.appendChild(b);
    var chg = h('button', 'ug-target__change', TXH('changeLabel'));
    chg.type = 'button';
    chg.onclick = function () {
      if (window.UpgradeDeal) window.UpgradeDeal.clearTarget();
      location.href = routeRoot() + 'pages/shop';
    };
    w.appendChild(chg);
    return w;
  }

  /* Everything priced on this screen is derived HERE, on each call: targetOf()
     re-prices the pinned target against the map, and stepResult() re-runs
     tradeUpOptions(), which re-asks window.UpgradePrices for every candidate
     variant. A second render against a fuller map therefore re-DECIDES which
     trade-up cards exist rather than redrawing the ones it chose last time --
     the property the repaint at the foot of this file relies on, because an
     unpriced option is dropped from the rail rather than drawn as a hotline
     card.

     `opts.silent` suppresses the scroll-into-view: a render the customer
     caused should carry them to the step they just answered, but a repaint
     they did not ask for must not move the page under them mid-read. */
  function render(opts) {
    var hadFocus = focusKey();   // Q21: the answer that had focus, if any
    root.innerHTML = '';
    // The confirmation showCreditApplied() wrote is gone with the innerHTML
    // above, so the DOM is render()'s again.
    creditApplied = false;

    var t = targetOf();
    if (t) root.appendChild(targetCard(t));

    var stripped = stripLabels();
    root.appendChild(stepCount(stripped.length));   // Lynkrs Q6 (W1a): "Step N of M"
    root.appendChild(steps(stripped));

    if (S.step >= 1) root.appendChild(stepDevice());
    if (S.step >= 2) root.appendChild(stepCondition());
    if (S.step >= 3) root.appendChild(stepBattery());
    if (S.step >= 4) root.appendChild(stepResult());

    restoreFocus(hadFocus);

    // Lynkrs Q21 (6 Oct 2026, T2): was an unconditional smooth scroll to the
    // middle of the window on every answer -- see reveal().
    var active = root.querySelector('.ug-step--active');
    if (active && S.step > 1 && !(opts && opts.silent)) {
      reveal(active);
    }
  }

  /* The strip's four stations, in order. Her words (E2, W1a), escaped. */
  function stripLabels() {
    return [TXH('stripDevice'), TXH('stripCondition'), TXH('stripBattery'), TXH('stripOffer')];
  }

  /* Lynkrs Q6 / decision D1 (batch 2, W1a, 6 Oct 2026). The strip showed
     four stations and no position, while the copy around it promised "three
     questions" for what takes six taps. The copy now says "a few quick
     questions" (templates/page.trade-in.json) and this line says where the
     customer is: "Step 2 of 4". M is the strip's own length, so it cannot
     disagree with the stations under it; N is the station marked current.
     Kept in code: a sentence with two numbers is a locale key in the Arabic
     copy (rule R4), not a setting. */
  function stepCount(total) {
    var n = Math.min(Math.max(S.step || 1, 1), total);
    var p = h('p', 'ug-progress__count');
    p.textContent = TT('tradein.step_count', { n: n, total: total });   // Arabic port (pA): a key (R4); edit everything: also a setting (TT: textContent)
    return p;
  }

  function steps(labels) {
    var wrap = h('ol', 'ug-progress');
    labels.forEach(function (t, i) {
      var li = h('li', 'ug-progress__item' + (S.step > i ? ' is-done' : '') + (S.step === i + 1 ? ' is-current' : ''));
      if (S.step === i + 1) li.setAttribute('aria-current', 'step');   // Q6: the same fact for a screen reader
      li.appendChild(h('span', 'ug-progress__num', String(i + 1)));
      li.appendChild(h('span', 'ug-progress__label', t));
      wrap.appendChild(li);
    });
    return wrap;
  }

  function card(title, n, active) {
    var s = h('section', 'ug-step' + (active ? ' ug-step--active' : ''));
    var head = h('div', 'ug-step__head');
    head.appendChild(h('span', 'ug-step__n', String(n)));
    head.appendChild(h('h3', 'ug-step__title', title));
    s.appendChild(head);
    return s;
  }

  function stepDevice() {
    var s = card(TXH('deviceTitle'), 1, S.step === 1);
    var body = h('div', 'ug-step__body');

    /* Launch punch list S9 (3), 27 Sep 2026. A new brand or model means the
       valuation in the shared state no longer describes the phone on screen,
       so it goes with the answers. It used to survive into the deal bar and
       the booking email, priced for a phone the customer had moved away from. */
    var brands = h('div', 'ug-brands');
    ['Apple', 'Samsung'].forEach(function (b) {
      // LF11 fix (29 Sep 2026, fix_now, audit w4k72rk5w): "is-on" is a
      // visual class only; aria-pressed is the same fact for a screen
      // reader, set in the same expression so the two can never disagree.
      var brandOn = S.brand === b;
      var btn = h('button', 'ug-brand' + (brandOn ? ' is-on' : ''), b);
      btn.type = 'button';
      btn.setAttribute('aria-pressed', brandOn ? 'true' : 'false');
      btn.onclick = function () {
        S.brand = b; S.series = null; S.storage = null; S.step = 1;
        if (window.UpgradeDeal) window.UpgradeDeal.clearTradein();
        render();
      };
      brands.appendChild(btn);
    });
    body.appendChild(brands);

    if (S.brand) {
      // Lynkrs Q7 (W1a): newest first, top model first -- sortModels() above.
      var list = sortModels(DATA.devices.filter(function (d) { return d.brand === S.brand && d.tradein; }));
      var grid = h('div', 'ug-models');
      list.forEach(function (d) {
        var modelOn = S.series === d.series;
        var btn = h('button', 'ug-model' + (modelOn ? ' is-on' : ''), d.label);
        btn.type = 'button';
        btn.setAttribute('aria-pressed', modelOn ? 'true' : 'false');
        btn.onclick = function () {
          S.series = d.series; S.storage = null;
          if (window.UpgradeDeal) window.UpgradeDeal.clearTradein();
          render();
        };
        grid.appendChild(btn);
      });
      body.appendChild(grid);

      var others = DATA.devices.filter(function (d) { return d.brand === S.brand && !d.tradein; }).length;
      if (others) {
        // Was a wa.me link with no number in it, which resolves to nothing.
        // Route to the one confirmed contact number instead.
        body.appendChild(h('p', 'ug-note', T('tradein.another_model', {
          link: '<a href="' + contactHref() + '" class="ug-link">' + T('tradein.call_our_hotline', { hotline: HOTLINE }) + '</a>'
        })));
      }
    }

    if (S.series) {
      var d2 = device();
      var pills = h('div', 'ug-storages');
      /* Launch punch list S9 (2), 27 Sep 2026. A storage the data file
         withdraws from quoting (tradein: false, e.g. iPhone 16 512GB) has no
         offers row, and picking it ended in the refusal copy -- "falls below
         the lowest offer we will quote" -- which is not why it has no price.
         It is not offered as a choice at all, the same rule the model list
         above applies to whole devices. */
      d2.variants.filter(function (v) { return v.tradein !== false; }).forEach(function (v) {
        var storageOn = S.storage === v.storage;
        var btn = h('button', 'ug-pill' + (storageOn ? ' is-on' : ''), v.storage);
        btn.type = 'button';
        btn.setAttribute('aria-pressed', storageOn ? 'true' : 'false');
        btn.onclick = function () { S.storage = v.storage; S.step = Math.max(S.step, 2); render(); };
        pills.appendChild(btn);
      });
      body.appendChild(pills);
    }

    s.appendChild(body);
    return s;
  }

  function stepCondition() {
    var s = card(TXH('conditionTitle'), 2, S.step === 2);
    var body = h('div', 'ug-step__body');
    var grid = h('div', 'ug-conditions');
    Object.keys(COND_LABELS).forEach(function (k) {
      var c = COND_LABELS[k];
      var condOn = S.condition === k;
      var btn = h('button', 'ug-cond' + (condOn ? ' is-on' : ''));
      btn.type = 'button';
      btn.setAttribute('aria-pressed', condOn ? 'true' : 'false');
      btn.appendChild(h('span', 'ug-cond__t', c.t));
      btn.appendChild(h('span', 'ug-cond__d', c.d));
      btn.onclick = function () { S.condition = k; S.step = Math.max(S.step, 2); render(); };
      grid.appendChild(btn);
    });
    body.appendChild(grid);

    /* The fault question. It exists in assets/upgrade-sell.js and did not
       exist here, so the same customer with the same handset got two
       different offers depending on which page they opened — the trade-in
       flow could never reach faulty:true at all. Same wording, same fault
       axis of the outcomes table (meta.offer_axes.fault), same place in the
       flow. */
    if (S.condition) {
      body.appendChild(h('p', 'ug-note', T('tradein.fault_question')));
      var faultRow = h('div', 'ug-storages');
      faultRow.setAttribute('role', 'radiogroup');
      faultRow.setAttribute('aria-label', TT('tradein.fault_label'));
      [{ k: 'no', t: T('tradein.fault_no') }, { k: 'yes', t: T('tradein.fault_yes') }].forEach(function (opt) {
        var isSel = S.faulty !== null && (opt.k === 'yes') === S.faulty;
        var btn = h('button', 'ug-pill' + (isSel ? ' is-on' : ''), opt.t);
        btn.type = 'button';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', isSel ? 'true' : 'false');
        btn.onclick = function () { S.faulty = (opt.k === 'yes'); S.step = Math.max(S.step, 3); render(); };
        faultRow.appendChild(btn);
      });
      body.appendChild(faultRow);
    }

    s.appendChild(body);
    return s;
  }

  /* Where the customer finds the figure (1 Oct 2026). Apple support 120745
     (17 Sep 2026): the iPhone 15 and later show Settings → Battery → Battery
     Health; the iPhone 14 and earlier show Battery Health &amp; Charging.
     Samsung shows Normal / Weak / Bad and no percentage (Samsung Egypt,
     3 Dec 2025), so a Samsung owner is told to press Don't know. */
  function batteryHelp() {
    if (S.brand !== 'Apple') return T('tradein.battery_help_samsung');
    var g = generationOf(S.series);
    return (g !== null && g >= 15)
      ? T('tradein.battery_help_iphone15')
      : T('tradein.battery_help_iphone14');
  }

  function stepBattery() {
    var s = card(TXH('batteryTitle'), 3, S.step === 3);
    var body = h('div', 'ug-step__body');
    body.appendChild(h('p', 'ug-hint', batteryHelp()));
    var pills = h('div', 'ug-storages');
    Object.keys(BATT_LABELS).forEach(function (k) {
      var battOn = S.battery === k;
      var btn = h('button', 'ug-pill' + (battOn ? ' is-on' : ''), BATT_LABELS[k]);
      btn.type = 'button';
      btn.setAttribute('aria-pressed', battOn ? 'true' : 'false');
      btn.onclick = function () { S.battery = k; S.step = 4; render(); };
      pills.appendChild(btn);
    });
    body.appendChild(pills);
    s.appendChild(body);
    return s;
  }

  function stepResult() {
    var q = quote();
    var s = card(TXH('resultTitle'), 4, true);
    var body = h('div', 'ug-step__body');
    if (!q) { body.appendChild(h('p', 'ug-note', TXH('storageHint'))); s.appendChild(body); return s; }

    /* Ruling R2, refusal side. The table publishes this combination as one we
       will not quote without having seen the phone (or publishes nothing
       usable for it, which is the same thing: we have no figure we can stand
       behind). Either way no number goes on screen and nothing is published
       into the shared state — a stale value from a previous answer must not
       survive into the deal bar. A buyer prices it by hand instead. */
    if (q.refused) {
      if (window.UpgradeDeal) window.UpgradeDeal.clearTradein();

      body.appendChild(h('p', 'ug-offer__k', T('tradein.refused_title')));
      /* The refusal is the table's (band X); this only says why, from the
         customer's own answers. Since 1 Oct 2026 a declared fault is never
         priced online, so the floor sentence would give it a false reason. */
      body.appendChild(h('p', 'ug-note',
        T(S.faulty ? 'tradein.refused_fault' : 'tradein.refused_floor', { phone: q.device.label + ' ' + q.variant.storage })));

      var call = document.createElement('a');
      call.className = 'ug-btn ug-btn--primary';
      call.href = contactHref();
      call.textContent = TT('tradein.call_our_hotline', { hotline: HOTLINE });
      call.style.display = 'inline-flex';
      call.style.marginBlockStart = '1rem';
      body.appendChild(call);

      body.appendChild(resetBtn());
      s.appendChild(body);
      return s;
    }

    // Publish the valuation so the deal bar and the product pages can use it.
    // Launch punch list S9 (4), 27 Sep 2026: the fault answer travels with it
    // (the same !! the lookup uses), so the booking email can state it. It was
    // priced here and then dropped, and staff never saw it.
    if (window.UpgradeDeal) {
      window.UpgradeDeal.setTradein({
        series: q.device.series, label: q.device.label, storage: q.variant.storage,
        condition: S.condition, battery: S.battery, faulty: !!S.faulty, value: q.cash
      }, { keep: true });   // Lynkrs PG-TradeIn-3 (W1a): kept on the device; this page says so (keptLine())
    }

    // Lynkrs PG-TradeIn-3 (W1a): a restored estimate says so first -- see savedNotice().
    var restored = savedNotice();
    if (restored) body.appendChild(restored);

    // Their handset, named once and used on every surface below, so the
    // summary, the cards and the confirmed card all call it the same thing.
    var mineName = q.device.label + ' ' + q.variant.storage;

    // If they came from a product page, the sum is the answer they want —
    // show it before the shopping options, then close with the booking step.
    var tgt = targetOf();
    if (tgt && window.UpgradeDeal) {
      var tot = window.UpgradeDeal.totals();
      if (tot) {
        var sumBox = h('div', 'ug-book');
        /* NOT id="book". sections/upgrade-book.liquid already renders the real
           booking form as <section id="book">, and this block is injected
           ABOVE it in document order on /pages/trade-in — so an id of "book"
           here captured every "#book" anchor on the page, including this
           block's own CTA and the sticky bar's, and they scrolled to the
           summary instead of to the form. */
        sumBox.id = 'ug-tradein-summary';
        var sumRows = h('div', 'ug-book__sum');
        /* The credit has one owner. UpgradeDeal.totals() publishes it as
           `credit` — the customer's phone value plus the bonus — so the bar,
           the product page and this summary all read the same field instead of
           each adding the two legs up again. The addition survives only as a
           fallback, for a stored deal written before that field existed. */
        var creditTotal = (typeof tot.credit === 'number' && isFinite(tot.credit))
          ? tot.credit
          : (tot.value + tot.bonus);
        /* The composition, not a single netted figure. Their phone and the
           bonus are added together into one credit first — that is what makes
           trading in beat selling — and only then is the target price set
           against it. The two credit lines carry their own status: the
           valuation is an estimate, the bonus is not. */
        sumRows.innerHTML =
          bookRow(T('tradein.summary_phone_estimate', { phone: mineName }), money(tot.value)) +
          (tot.bonus ? bookRow(T('tradein.bonus_guaranteed'), T('common.money', { amount: plusFig(tot.bonus) })) : '') +
          (tot.bonus ? bookRow(T('tradein.summary_total_credit'), money(creditTotal)) : '') +
          // Colour variants, ported 27 Sep 2026: targetLabel() adds the colour, escaped.
          bookRow(T('tradein.summary_target_price', { device: targetLabel(tgt) }), money(tot.price)) +
          // R1, same words as the sticky bar and the booking form: all three of
          // these render at once on this page and must not describe the same
          // number two different ways. It is estimated because the valuation
          // above it is — never because the bonus is.
          /* tot.covered (assets/upgrade-deal.js totals()): the credit exceeds
             the price. The sticky bar renders in the same viewport, so printing
             0 here would put the contradiction inside one scroll. Same sentence
             as the bar, byte for byte. Omar, 28 Sep 2026 (LOG-021): the person
             at the door pays the customer the difference. bookRow() does not
             escape, and this literal carries no HTML metacharacters. */
          // F6, 29 Sep 2026 (fix_now, prices-on-page P7): tot.surplus > 0
          // keeps the existing sentence; an exact match (surplus === 0) has
          // no difference to pay, so it says "Nothing to pay" instead.
          /* Edit everything (7 Oct 2026): T() reads these three from the deal
             bar's words when that section has them (SHARED_WITH_DEAL,
             dealSays()), so one edit changes both places. */
          (tot.covered
            ? (tot.surplus > 0
                ? bookRow(T('tradein.estimated_balance'), T('tradein.covered_full'), 'ug-book__row--total ug-book__row--covered')
                : bookRow(T('tradein.estimated_balance'), T('tradein.covered_exact'), 'ug-book__row--total ug-book__row--covered'))
            : bookRow(T('tradein.estimated_balance_to_pay'), money(tot.pay), 'ug-book__row--total'));
        /* valU (LOG-023): the trade-in pays the down payment and the rest is
           spread over 12 months. The wording is the deal module's, so this row
           and the sticky bar cannot describe the plan two different ways.
           Nothing to finance on a covered deal. */
        var valu = (!tot.covered && window.UpgradeDeal.valuPlan) ? window.UpgradeDeal.valuPlan(tot.price, tot.pay) : null;
        if (valu && window.UpgradeDeal.valuTerms) {
          // Edit everything: the terms are the bar's words now (settings), so escaped here; today's carry nothing to escape.
          sumRows.innerHTML += bookRow(T('tradein.or_with_valu'), escText(window.UpgradeDeal.valuTerms(valu)), 'ug-book__row--fin');
        }
        sumBox.appendChild(sumRows);
        /* What to bring. The box used to sit in the middle of this list,
           between the phone and the national ID, reading as a third
           requirement -- and the branch had no way to honour it either way.
           There is no box factor anywhere in the pricing engine (grep "box"
           in assets/upgrade-tradein-data.json: zero hits), so a seller who
           arrives without one cannot be re-quoted against any published
           figure, and the page promises two lines above that the inspected
           price is the quoted price with "nothing hidden". A requirement that
           cannot change the price is not a requirement; listing it beside the
           ID, which genuinely does block payout, left the seller no way to
           tell the two apart. Demoted to what it actually is: welcome, not
           needed, and priced at nothing. */
        /* 28 Sep 2026 (LOG-021): a trade-in ends in an order, and the phone is
           checked at the door by the person who delivers the new one. So this
           note says what happens there, and what to have ready.
           29 Sep 2026 (LOG-028, review fix, dated per finding 10): both
           branches below dropped ", plus delivery" -- delivery is free, its
           cost is already in the phone's price. */
        sumBox.appendChild(h('p', 'ug-note',
          // The note points at "the balance above". On a covered deal there is
          // no balance above any more, so the sentence has to follow the row.
          // F6, 29 Sep 2026 (fix_now, prices-on-page P7): same surplus branch
          // as the row above -- an exact match has nothing to pay back.
          // Arabic phase 3: three whole sentences (EN tradein.summary_note_*),
          // not a lead and a tail glued round a clause.
          T(tot.covered
            ? (tot.surplus > 0
                ? 'tradein.summary_note_covered'
                : 'tradein.summary_note_exact')
            : 'tradein.summary_note_balance', { phone: q.device.label })));
        // Lynkrs Q10 (batch 2, W1b, 6 Oct 2026): what to have ready is the one checklist, snippets/upgrade-checklist.liquid,
        // rendered as a <template> by sections/upgrade-tradein.liquid. It replaces the box and ID/sign-out words the note above carried.
        var ugChecklist = document.querySelector('template[data-ug-checklist-template="tradein"]');
        if (ugChecklist && ugChecklist.content) sumBox.appendChild(ugChecklist.content.cloneNode(true));
        /* The order button. The same action as the sticky bar's, through the
           same function, so the notes written onto the order cannot depend on
           which button was pressed. With no orderable variant pinned (a phone
           pinned from a trade-up card, or a sold-out grade) it goes to the
           phone's page instead, where Add to cart carries the trade-in. */
        var dealApi = window.UpgradeDeal;
        var canOrder = !!(tgt.variantId && dealApi.order);
        var go = document.createElement(canOrder ? 'button' : 'a');
        go.className = 'ug-btn ug-btn--primary';
        // LF5 fix (29 Sep 2026, fix_now, audit w4k72rk5w): a refused order
        // used to say why only through window.alert(), which some in-app
        // webviews (and this build's own audit tooling) suppress -- the
        // button looked dead with no explanation. This inline
        // role="status" paragraph, next to the button, carries the same
        // wording, plus a "View cart" link; starts hidden and empty.
        var goMsg = h('p', 'ug-note ug-note--error');
        goMsg.setAttribute('role', 'status');
        goMsg.setAttribute('aria-live', 'polite');
        goMsg.hidden = true;
        if (canOrder) {
          var orderLabel = dealApi.orderLabel ? dealApi.orderLabel() : T('tradein.order_cta');
          go.type = 'button';
          go.textContent = orderLabel;
          go.addEventListener('click', function () {
            goMsg.hidden = true;
            goMsg.textContent = '';
            // The phone this box names must still be the one pinned: if the
            // customer has since chosen another, go to it rather than order this.
            var now = dealApi.get ? dealApi.get().target : null;
            if (!now || String(now.variantId || '') !== String(tgt.variantId)) {
              window.location.href = dealApi.targetHref ? dealApi.targetHref(now || tgt) : routeRoot() + 'pages/shop';
              return;
            }
            go.disabled = true;
            go.textContent = TT('tradein.adding');
            dealApi.order().catch(function (e) {
              go.disabled = false;
              go.textContent = orderLabel;
              goMsg.textContent = (dealApi.orderFailMessage ? dealApi.orderFailMessage(e)
                : T('tradein.order_failed', { hotline: HOTLINE })) + ' ';
              var viewCart = document.createElement('a');
              viewCart.href = routeRoot() + 'cart';
              viewCart.textContent = TT('tradein.view_cart');
              goMsg.appendChild(viewCart);
              goMsg.hidden = false;
            });
          });
        } else {
          go.href = dealApi.targetHref ? dealApi.targetHref(tgt) : routeRoot() + 'pages/shop';
          go.textContent = TT('tradein.choose_phone');
        }
        go.style.marginBlockStart = '1rem';
        go.style.display = 'inline-flex';
        sumBox.appendChild(go);
        sumBox.appendChild(goMsg);
        body.appendChild(sumBox);
      }
    }

    /* Real trade-up targets first: the headline bonus must never exceed what
       bonusFor() can actually deliver against one of them. No target, no hero.

       We keep the option that carries the best bonus, not just its size. The
       headline names it, because "up to" is exactly the word this bonus must
       never wear: the bonus is fixed the moment a device is chosen, and the
       only thing varying across the rail is WHICH device. Naming the device
       says that without making the bonus itself sound conditional. */
    /* No usable rate, no bonus figure anywhere on this screen — the same
       suppression assets/upgrade-sell.js and assets/upgrade-deal.js apply.
       Every card below states a guaranteed amount, so a card is only built for
       an option whose bonus is a real number. */
    var rate = bonusRate();
    var ups = rate ? tradeUpOptions(q).filter(function (u) { return bonusFor(u.list, u.code) !== null; }) : [];
    /* The prices these cards are built from come from Shopify. If the map is
       not on the page or carries nothing usable, there are no cards — not
       because nothing fits the customer's valuation, but because we do not
       know what anything costs. That is a different sentence to the customer,
       and it is the same one an unreadable bonus rate gets: the route stays
       open, the figure does not appear. */
    var priceMap = priceApi();
    var pricesOffline = !(priceMap && priceMap.count > 0);
    var best = null;
    var maxBonus = 0;
    ups.forEach(function (u) {
      var b = bonusFor(u.list, u.code);
      if (b !== null && b > maxBonus) { maxBonus = b; best = u; }
    });

    var offers = h('div', 'ug-offers');

    var cash = h('div', 'ug-offer');
    cash.appendChild(h('span', 'ug-offer__k', TXH('cashHeading')));
    cash.appendChild(h('strong', 'ug-offer__v', fig(q.cash) + ' <small>' + T('common.currency') + '</small>'));
    if (TX('cashLine')) cash.appendChild(h('span', 'ug-offer__s', TXH('cashLine')));   // E2 (W1a): her words; emptied, left out
    /* 28 Sep 2026 (LOG-021 review): the way to actually sell. The booking form
       is the sell's alone and shows only when no phone is pinned to buy, and a
       product-page visit always pins one -- so without this the tile was a
       dead end. It unpins the phone (the valuation stays) and goes to the
       form, which then shows as a sell. The trade-in summary above is removed
       because it no longer describes the deal. */
    var sellLink = document.createElement('a');
    sellLink.className = 'ug-offer__link';
    sellLink.textContent = TX('sellLink');
    sellLink.href = document.querySelector('[data-ug-book]') ? '#book' : routeRoot() + 'pages/sell';
    sellLink.addEventListener('click', function () {
      var stale = document.getElementById('ug-tradein-summary');
      if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
      if (window.UpgradeDeal && window.UpgradeDeal.clearTarget) window.UpgradeDeal.clearTarget();
      /* A page opened from a product page carries ?target=; targetOf() would
         re-pin that phone on the next render and undo the choice (28 Sep
         audit). Drop the three target parameters from the address. */
      try {
        var q = new URLSearchParams(location.search);
        ['target', 'targetGrade', 'targetStorage'].forEach(function (k) { q.delete(k); });
        var qs = q.toString();
        history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + '#book');
      } catch (e) { /* no history API: the unpin above still stands for this render */ }
      unpricedTarget = null;
    });
    cash.appendChild(sellLink);
    offers.appendChild(cash);

    /* The two doors, side by side, which is the only place the customer can
       see that the same phone is worth more through one of them. The hero is
       their valuation PLUS the bonus, shown as a sum, never as one figure.

       LF9 fix (29 Sep 2026, fix_now, audit w4k72rk5w): this tile used to be
       built from `best`/`maxBonus` -- the biggest bonus anywhere on the
       rail -- even when a phone was ALREADY pinned (targetOf(), the same
       `tgt`/`tot` the summary above this tile reads). That could name a
       different phone with a different credit than the summary two lines
       up, on the same screen. When a real, priced target is pinned, the
       tile is now built from THAT target and `tot.bonus`/`tot.credit` --
       the SAME shared totals() the summary uses, never recomputed against
       a different grade or price basis (bonusFor() elsewhere on this rail
       prices every card at CARD_GRADE; a pinned target keeps its OWN
       grade, exactly as the summary already does). `best`/`maxBonus`
       remain the fallback: nothing pinned, or the pin could not be priced. */
    var tileSource = null;
    var tileFromCard = false, heroTile = null, heroLine = null, cardsDrawn = 0;   // V1.2 AUDIT FIX 2, 11 Oct 2026 (flows-03): see the end of the rail below
    if (tgt && typeof tgt.price === 'number' && tgt.price > 0 && tot &&
        typeof tot.bonus === 'number' && tot.bonus > 0 && typeof tot.credit === 'number') {
      tileSource = { label: tgt.label, storage: tgt.storage, bonus: tot.bonus, credit: tot.credit };
    } else if (ups.length && best) {
      tileSource = { label: best.label, storage: best.storage, bonus: maxBonus, credit: q.cash + maxBonus };
      tileFromCard = true;   // V1.2 AUDIT FIX 2 (flows-03): this box names a card on the rail
    }
    if (tileSource) {
      var credit = h('div', 'ug-offer ug-offer--hero');
      credit.appendChild(h('span', 'ug-offer__k', T('tradein.trade_towards', { device: tileSource.label + ' ' + tileSource.storage })));
      credit.appendChild(h('strong', 'ug-offer__v', fig(tileSource.credit) + ' <small>' + T('common.currency') + '</small>'));
      heroLine = credit.appendChild(h('span', 'ug-offer__s',
        T('tradein.hero_explainer', { value: fig(q.cash), bonus: fig(tileSource.bonus) })));
      offers.appendChild(credit);
      heroTile = credit;   // V1.2 AUDIT FIX 2 (flows-03)
    }

    body.appendChild(offers);

    /* The estimate caveat, on screen and beside the figures.

       It used to live only inside the <details> below, which is collapsed by
       default, so the trade-in page showed a customer a hard EGP number with
       nothing next to it qualifying the number -- the caveat was one click
       away. The sell page does not have that problem: it renders the same
       engine's figures with a permanent static note above the fold as well as
       the collapsed copy. Same numbers, same uncertainty, two standards of
       disclosure, and trade-in was the weaker one.

       The split is the settled commercial model and must not blur: the
       customer's phone is an ESTIMATE until it has been inspected; the
       trade-up bonus is GUARANTEED, because it is a function of the device
       being bought and of nothing about the phone being traded in. Never
       "up to" on the bonus.
       Launch punch list S7, 27 Sep 2026: "70-step" removed here and in the
       panel below. The 70-step inspection is what Upgrade does to the phones
       it sells; the customer's own phone is checked at a free inspection. */
    body.appendChild(h('p', 'ug-note', T('tradein.estimate_caveat')));

    /* WHAT WE PRICED — not how we priced it. Ruling, 22 September 2026.

       This block used to itemise the offer: a starting figure for the device,
       then a line for every defect that reduced it. Both are the company's
       margin structure, and neither is any longer in the browser to print —
       the engine looks up an outcome and never sees an input. Do not restore
       either, under any label: assets/ is served unauthenticated.

       The heading moved with the content. "How we got to this number"
       described arithmetic that is no longer shown, and a panel that promises
       working and then shows none is worse than one that never promised it.

       What survives is the trust function, which was never the arithmetic: the
       customer can read back exactly what we priced, in their own answers, and
       say so if it is wrong before anyone collects a phone. */
    var det = document.createElement('details');
    det.className = 'ug-working';
    var sum = document.createElement('summary');
    sum.textContent = TX('pricedHeading');
    det.appendChild(sum);
    var tbl = h('table', 'ug-table');
    tbl.innerHTML = '<tbody><tr class="ug-table__tot"><th>' + T('tradein.your_phone_value') + '</th><td>' +
      fig(q.cash) + '</td></tr></tbody>';
    det.appendChild(tbl);
    det.appendChild(h('p', 'ug-note', T('tradein.what_we_priced_summary', { summary: pricedSummary(q) })));
    det.appendChild(h('p', 'ug-note', T('tradein.inspection_confirms')));
    /* The rung the offer sits on, as the table publishes it, shown rather than
       hidden: it is the reason the number above is one we will stand behind. */
    if (q.band && q.band.label) {
      det.appendChild(h('p', 'ug-note', T('tradein.ladder_band', { band: bandName(q.band) })));
    } else if (q.band) {
      det.appendChild(h('p', 'ug-note', T('tradein.ladder_below_average')));
    }
    body.appendChild(det);

    /* the sell half of the loop — reuses the same ups/maxBonus computed above */
    if (ups.length) {
      body.appendChild(h('h4', 'ug-subhead', TXH('railHeading')));
      var grid = h('div', 'ug-upgrades');
      ups.forEach(function (u) {
        var b = bonusFor(u.list, u.code);
        /* Unclamped on purpose. tradeUpOptions() now refuses to build a card
           whose credit covers the device, so this can no longer go negative —
           and if it ever does again, the card is dropped and the console says
           so rather than the customer being shown a zero. The old
           Math.max(0, ...) here is what turned that arithmetic into a promise
           of a free phone; a clamp that hides a defect is worse than no clamp. */
        var payable = u.list - q.cash - b;
        if (!(payable > 0)) {
          if (window.console && typeof console.warn === 'function') {
            console.warn('[upgrade-tradein] suppressed a trade-up card: credit ' +
              (q.cash + b) + ' covers ' + u.label + ' ' + u.storage + ' at ' + u.list +
              ' (balance ' + payable + '). The eligibility band in tradeUpOptions() ' +
              'should have excluded this.');
          }
          return;
        }
        var cardEl = h('div', 'ug-up');
        cardEl.appendChild(h('span', 'ug-up__grade', gradeName(CARD_GRADE)));
        cardEl.appendChild(h('span', 'ug-up__name', u.label + ' ' + u.storage));
        cardEl.appendChild(h('span', 'ug-up__list', money(u.list)));
        /* Three lines instead of two, and the difference is the point: the
           card used to net the bonus away into one "you pay" figure, which
           hid the only reason to trade in rather than sell. The credit is
           built in front of the customer — their phone, plus the bonus that
           this particular device carries, added up — and the balance follows
           from it. Same grid, same classes; a row, not a table. */
        var br = h('div', 'ug-up__calc');
        br.innerHTML =
          '<span>' + T('tradein.your_phone_estimate', { phone: mineName }) + '</span><b>' + fig(q.cash) + '</b>' +
          '<span>' + T('tradein.bonus_guaranteed') + '</span><b>' + plusFig(b) + '</b>' +
          '<span>' + T('tradein.total_credit') + '</span><b>' + fig(q.cash + b) + '</b>';
        cardEl.appendChild(br);
        cardEl.appendChild(h('span', 'ug-up__pay', T('tradein.card_balance', { amount: fig(payable) })));
        var go = h('button', 'ug-cta', TXH('chooseLabel'));
        go.type = 'button';
        go.onclick = function () { showCreditApplied(cardEl, u, mineName, q.cash, b); };
        cardEl.appendChild(go);
        grid.appendChild(cardEl);
        cardsDrawn++;   // V1.2 AUDIT FIX 2 (flows-03)
      });
      body.appendChild(grid);
      /* The rail suggests; it does not limit. These three are the closest
         newer phones within the customer's range, and someone who wants a
         different one should not have to take what was picked for them.
         The valuation is already in shared state, so the catalogue keeps it.
         2 Oct 2026 (Omar): testers missed this as a text link at the foot of
         the result, under the bonus paragraph. It now sits directly under the
         cards, as its own highlighted block with a real button. */
      body.appendChild(browseBlock());
      body.appendChild(h('p', 'ug-note', T('tradein.bonus_explainer')));
    } else if (rate && stockHidden > 0) {
      /* TRADE-UP IN STOCK, 11 Oct 2026 (2026-10-11_tradeup-in-stock/CHANGES.md):
         there ARE newer phones in range, but none of them is in stock (or its
         stock is unknown). No rail and no heading: neither "Trade up to" over
         nothing nor the "newest phone" sentence, which would be untrue here.
         The way to the shop and the bonus note stay, exactly as they follow
         the rail above. No new words. */
      body.appendChild(browseBlock());
      body.appendChild(h('p', 'ug-note', T('tradein.bonus_explainer')));
    } else if (rate && !pricesOffline) {
      /* The rail is empty and nothing is wrong: we simply do not sell anything
         NEWER than the phone being handed in, within range. Since 24 September
         2026 the rail only ever suggests a later generation, so this is the
         normal state for the newest devices in the catalogue -- today the three
         newest Samsungs. Saying nothing here would read as the page failing.
         Never suggest an older or same-generation phone to fill the space:
         that is the rule this branch exists to keep. */
      body.appendChild(h('h4', 'ug-subhead', T('tradein.trade_up_instead')));
      body.appendChild(h('p', 'ug-note', T('tradein.newest_phone', {
        device: q.device.label,
        shop_link: '<a href="' + routeRoot() + 'pages/shop" class="ug-link">' + T('tradein.browse_full_range') + '</a>',
        hotline_link: '<a href="' + contactHref() + '" class="ug-link">' + T('tradein.call_our_hotline_lower', { hotline: HOTLINE }) + '</a>'
      })));
    } else if (!rate || pricesOffline) {
      /* The route stays open; the number does not appear. Same behaviour as
         the bridge in assets/upgrade-sell.js when the rate cannot be read,
         and now also when Shopify has told us no price at all. */
      body.appendChild(h('h4', 'ug-subhead', T('tradein.trade_up_instead')));
      body.appendChild(h('p', 'ug-note', T('tradein.put_toward', {
        device: q.device.label,
        // FIX PASS, 7 Oct 2026 (edit-everything-7oct/fixes/FIXES.md D25): the link's words are the field
        // tool_browse_button (TX('browseButton')), as on the "Different phone" box's own button.
        shop_link: '<a href="' + routeRoot() + 'pages/shop" class="ug-link">' + TXH('browseButton') + '</a>',
        hotline_link: '<a href="' + contactHref() + '" class="ug-link">' + T('tradein.call_our_hotline_lower', { hotline: HOTLINE }) + '</a>'
      })));
    }

    /* V1.2 AUDIT FIX 2, 11 Oct 2026 (flows-03; 2026-10-11_v1.2-audit/FIXES-2.md):
       no card drawn, no words that point to one. Since TRADE-UP IN STOCK
       (11 Oct) the rail is often empty, because nothing newer is in stock. With
       a phone pinned from its product page the "towards" box above still
       showed, built from that phone (LF9), and its line under the figure ended
       "each one below shows its own" over nothing. So, with no card on the
       rail: that line is left out (the summary above already gives the same
       estimate plus bonus as credit), and a box built from a card (best) is
       left out whole, because the card it names is not there. A pinned phone's
       box keeps its heading and figure: they name that phone, as the summary
       does. Removed before this step is returned, so neither is ever painted.
       No new words; the same in every language. Nothing here reads or changes
       a price, a bonus or a balance. */
    if (heroTile && cardsDrawn === 0) {
      if (tileFromCard) offers.removeChild(heroTile);
      else if (heroLine) heroTile.removeChild(heroLine);
    }

    // Lynkrs PG-TradeIn-3 (W1a): a new estimate says it is kept -- see keptLine().
    var kept = keptLine(q.cash);
    if (kept) body.appendChild(kept);

    // A restored estimate already has its "Start again", in the notice at the top.
    if (!restored) body.appendChild(resetBtn());

    s.appendChild(body);
    return s;
  }

  /* Launch punch list S9 (3), 27 Sep 2026: "Start again" clears the published
     valuation as well as the answers. The pinned target stays -- it is the
     phone they came to buy, not part of the valuation. */
  /* The "pick any phone" way out of the trade-up rail, as a highlighted block
     (.ug-browse, upgrade-core.css). Shared shape with assets/upgrade-sell.js. */
  function browseBlock() {
    var box = h('div', 'ug-browse');
    box.appendChild(h('p', 'ug-browse__k', TXH('browseTitle')));
    var browseText = TX('browseText');   // E2 (W1a): her words; emptied, left out
    if (browseText) box.appendChild(h('p', 'ug-browse__note', escText(browseText)));
    var go = h('a', 'ug-btn ug-btn--primary ug-browse__btn', TXH('browseButton'));
    go.href = routeRoot() + 'pages/shop';
    box.appendChild(go);
    return box;
  }

  function resetBtn() {
    var reset = h('button', 'ug-reset', TXH('resetLabel'));
    reset.type = 'button';
    reset.onclick = function () {
      S = blankState();
      if (window.UpgradeDeal) window.UpgradeDeal.clearTradein();
      render();
    };
    return reset;
  }

  /* ---- boot -------------------------------------------------------------- */

  /* Launch punch list S10, 27 Sep 2026. Where this visit came from, recorded
     once per tab session for the booking email's contact[Source] field. Same
     key, same shape and same first-write-wins rule as captureSource() in
     sections/upgrade-book.liquid, which is the reader and the canonical copy.
     It is repeated here because this tool is mounted on the homepage, where
     visits start and the booking form is not: without it a visit that begins
     there and books on /pages/trade-in reads "Landing page: not recorded".
     Path, referrer host and utm_* only. No app, no cookie, nothing sent. */
  (function captureSource() {
    try {
      var KEY = 'ug.source.v1';
      var old = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (old && old.at && (Date.now() - old.at) <= 24 * 60 * 60 * 1000) return;
      var refHost = '';
      try { refHost = document.referrer ? new URL(document.referrer).hostname : ''; } catch (e) { refHost = ''; }
      var internal = refHost !== '' && refHost === location.hostname;
      var q = new URLSearchParams(location.search);
      var utm = {};
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(function (k) {
        var v = q.get(k);
        if (v) utm[k] = v.slice(0, 100);
      });
      sessionStorage.setItem(KEY, JSON.stringify({
        landing: location.pathname, referrer: internal ? '' : refHost,
        internal: internal, utm: utm, at: Date.now()
      }));
    } catch (e) { /* storage blocked: the booking form records its own page view */ }
  })();

  fetch(root.dataset.upgradeTradein)
    .then(function (r) { return r.json(); })
    .then(function (json) {
      DATA = json;
      // We have just fetched the file assets/upgrade-deal.js would otherwise
      // fetch a second time for the trade-up bonus rate. Hand the meta block
      // straight over, BEFORE the first render: totals() returns null until a
      // rate is published, and the booking summary below reads totals()
      // inline. Without this the summary is skipped for good whenever
      // upgrade-deal.js's own fetch has not resolved (or fails).
      if (window.UpgradeDeal && window.UpgradeDeal.setRates) window.UpgradeDeal.setRates(json.meta);
      restoreFromLiveTradein();
      restoreFromSaved();   // Lynkrs PG-TradeIn-3 (W1a): only when this tab has no live valuation
      reconcileUrlTarget();
      render();
    })
    .catch(function () {
      root.innerHTML = '<p class="ug-note">' + TXH('unavailable') + '</p>';
    });

  /* ---- repaint when the live prices land ---------------------------------
     This tool PAINTS IMMEDIATELY and repaints; it never waits on the price
     map. Decision of 22 September 2026, the pattern assets/upgrade-catalogue.js
     already proves at its :273 listener: whenReady() can hold a consumer for
     the whole walk budget against a hanging endpoint, and a page that never
     shows a price is a worse outage than one that briefly shows the hotline.
     The boot render above therefore stays exactly where it is, immediately
     after setRates(), and this listener corrects the screen afterwards.

     Two different things get corrected, and the second is the one with no
     visible symptom:

       - the "Trading up to" card, which does show a hotline state, so a
         customer can at least see that we could not price it; and
       - the trade-up rail, which does NOT. tradeUpOptions() drops a variant
         it cannot price rather than drawing a hotline card for it, so above
         the 50-product Liquid seed in snippets/upgrade-price-map.liquid the
         customer would simply be offered fewer devices than we sell, with
         nothing on screen saying so. render() re-derives that list from the
         map instead of redrawing the cards already chosen, so the missing
         cards appear.

     Guards:
     - no DATA means the offer file has not arrived (or failed): nothing is
       painted yet and the boot render above will read the settled map.
     - a confirmed "credit applied" card is the one piece of this screen
       render() did not draw, and the customer's choice is recorded nowhere
       else, so it is not torn down. The pinned target above it is refreshed
       in place instead -- that is the only priced thing left on screen that
       the walk could have changed.
     - the "What we priced" disclosure is reopened if it was open: an
       unrequested repaint must not collapse what the customer was reading.

     Feature-detected throughout. The event may never fire (a price map with
     no walk), and document.addEventListener is checked before use. Nothing
     here reads api.complete: the event is the signal, and a surface testing
     `!api.complete` would read a map that never publishes the field as
     permanently unsettled. */
  function repaintForPrices() {
    if (!DATA) return;

    if (creditApplied) {
      var t = targetOf();
      if (!t) return;
      var fresh = targetCard(t);
      var old = root.querySelector('.ug-target');
      if (old && old.parentNode) {
        old.parentNode.replaceChild(fresh, old);
      } else {
        // insertBefore(node, null) appends, so this is safe on an empty root.
        root.insertBefore(fresh, root.firstChild);
      }
      return;
    }

    var det = root.querySelector('details.ug-working');
    var wasOpen = !!(det && det.open);

    render({ silent: true });

    if (wasOpen) {
      var again = root.querySelector('details.ug-working');
      if (again) again.open = true;
    }
  }

  if (typeof document.addEventListener === 'function') {
    document.addEventListener('ug:prices:ready', repaintForPrices);
  }

  /* ---- the theme editor (Lynkrs batch 2, W1a, 6 Oct 2026) ---------------
     Each time the content lead changes a field of this section, the theme
     editor re-renders it: the mount comes back empty and its <script> tag is
     not run again, so the preview lost the whole tool until a reload. On
     Shopify's shopify:section:load (fired only inside the theme editor, never
     for a customer: the same hook upgrade-pdp.js, upgrade-catalogue.js and
     upgrade-calc.js use) this file runs once more against the new mount, so
     her words show at once. One listener per page (the flag), however many
     times it has run. */
  if (typeof document.addEventListener === 'function' && typeof window !== 'undefined' && !window.__ugTradeinEditorHook) {
    window.__ugTradeinEditorHook = true;
    document.addEventListener('shopify:section:load', function (event) {
      var mount = (event && event.target && typeof event.target.querySelector === 'function')
        ? event.target.querySelector('[data-upgrade-tradein]') : null;
      var me = document.querySelector('script[src*="upgrade-tradein.js"]');
      if (!mount || !me || !document.body) return;
      var again = document.createElement('script');
      again.src = me.getAttribute('src');
      document.body.appendChild(again);
    });
  }
})();
